/* global globalThis */
/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from "prop-types";
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl, // Keep FormControl as it's used for Select
  FormControlLabel,
  Tooltip,
  Checkbox,
  Menu,
  Radio,
  RadioGroup,
  Snackbar,
  Alert,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import {
  GripVertical,
  Plus,
  Trash2,
  Edit,
  Eye,
  MoreVertical,
  CornerDownRight,
} from "lucide-react";
import { useNavigate, Link, useParams } from "react-router-dom";
import {
  fetchLatestAssessmentGroups,
  createAssessmentGroup,
  updateAssessmentGroup,
  saveAssessmentDraftBeacon,
  fetchAssessmentGroupById,
  validateAssessmentGroupSchema,
  fetchAssessmentTemplateByType,
  parseSchemaPayload,
  fetchAssessmentReferenceData,
} from "../../api/assessment";
import { useLoading } from "../../context/LoadingContext";
import CommonTable from "../../components/common/table/commonTable";
import { createHeaderWithSort } from "../../components/common/table/columns";

const hasSectionsAndQuestions = (template) => {
  if (!template?.sections) return false;
  const sectionKeys = Object.keys(template.sections);
  if (sectionKeys.length === 0) return false;
  return sectionKeys.some((sKey) => {
    const fields = template.sections[sKey]?.fields;
    return fields && Object.keys(fields).length > 0;
  });
};

const getTemplateDisplayName = (template) => {
  if (template?.name) return template.name;
  if (template?.form?.title) return template.form.title;
  if (template?.templateName) return template.templateName;
  if (template?.groupName) return template.groupName;

  const rawId = String(
    template?.programId ||
      template?.program ||
      template?.groupType ||
      "general",
  );
  const norm = rawId.toLowerCase().replaceAll(" ", "_").replaceAll("’", "");

  if (norm.includes("lifestyle") || rawId.toUpperCase().includes("LIFESTYLE")) {
    return "Lifestyle, Barriers and Functional Assessment";
  }

  if (
    norm.includes("care_plan") ||
    norm.includes("careplan") ||
    rawId.toUpperCase().includes("CARE_PLAN")
  ) {
    return "Care Plan, Coordination and Completion";
  }

  const fixedNames = {
    general: "General Assessment",
    global: "General Assessment",
    other: "General Assessment",
    other_general: "General Assessment",
    lifestyle: "Lifestyle, Barriers and Functional Assessment",
    lifestyle_barriers_functional:
      "Lifestyle, Barriers and Functional Assessment",
    lifestyle_barriers_and_functional:
      "Lifestyle, Barriers and Functional Assessment",
    lifestyle_barriers_functional_assessment:
      "Lifestyle, Barriers and Functional Assessment",
    lifestyle_barriers_and_functional_assessment:
      "Lifestyle, Barriers and Functional Assessment",
    care_plan: "Care Plan, Coordination and Completion",
    care_plan_coordination_completion: "Care Plan, Coordination and Completion",
    care_plan_coordination_and_completion:
      "Care Plan, Coordination and Completion",
    care_plan_coordination_completion_assessment:
      "Care Plan, Coordination and Completion",
    care_plan_coordination_and_completion_assessment:
      "Care Plan, Coordination and Completion",
  };

  return fixedNames[norm] || rawId;
};

const refreshTemplatesList = async (setTemplates, errorMessage) => {
  try {
    const listData = await fetchLatestAssessmentGroups();
    setTemplates(formatTemplatesForDisplay(listData));
  } catch (error) {
    console.error(errorMessage, error);
  }
};

const buildTemplatesCache = (updatedGroup) => {
  const newCache = {};
  updatedGroup.templates?.forEach((t) => {
    const parsedPayload = parseSchemaPayload(t.schemaPayload);
    newCache[t.formType.toLowerCase()] = {
      ...t,
      ...(parsedPayload || null),
      version: updatedGroup.groupVersion,
      programId: updatedGroup.programId,
      groupName: updatedGroup.groupName,
      groupType: updatedGroup.groupType,
    };
  });
  return newCache;
};

const formatTemplatesForDisplay = (rawList = []) => {
  const list = Array.isArray(rawList) ? rawList : [];

  const isGeneralGroup = (g) => {
    const name = String(g.groupName || "").toLowerCase();
    const pid = String(g.programId || "").toUpperCase();
    const type = String(g.groupType || "").toUpperCase();
    if (
      name.includes("lifestyle") ||
      name.includes("care plan") ||
      pid.includes("LIFESTYLE") ||
      pid.includes("CARE_PLAN")
    ) {
      return false;
    }
    return (
      pid === "GLOBAL" ||
      pid === "GENERAL" ||
      pid === "OTHER" ||
      type === "GENERAL" ||
      name.includes("general")
    );
  };

  const isLifestyleGroup = (g) => {
    const name = String(g.groupName || "").toLowerCase();
    const pid = String(g.programId || "").toUpperCase();
    return name.includes("lifestyle") || pid.includes("LIFESTYLE");
  };

  const isCarePlanGroup = (g) => {
    const name = String(g.groupName || "").toLowerCase();
    const pid = String(g.programId || "").toUpperCase();
    return name.includes("care plan") || pid.includes("CARE_PLAN");
  };

  // 1. General Assessment (Step 1 stage)
  const generalGroups = list.filter(isGeneralGroup);
  const formattedGeneral =
    generalGroups.length > 0
      ? generalGroups.map((g) => ({
          ...g,
          groupName: g.groupName || "General Assessment",
        }))
      : [
          {
            id: "general_assessment_default",
            groupName: "General Assessment",
            programId: "GLOBAL",
            groupType: "GENERAL",
            groupVersion: 20,
            status: "ACTIVE",
            updatedAt: new Date().toISOString(),
          },
        ];

  // 2. Program-Specific Sub-items (Step 2 stage)
  const programGroups = list.filter(
    (g) => !isGeneralGroup(g) && !isLifestyleGroup(g) && !isCarePlanGroup(g),
  );
  const programSubItems = programGroups.map((g) => ({
    ...g,
    isProgramSubItem: true,
  }));

  const categoryHeader = {
    id: "category_header_program_specific",
    isCategoryHeader: true,
    groupName: "Program Specific Assessments",
  };

  // 3. Step 3: Lifestyle, Barriers and Functional Assessment
  const lifestyleGroups = list.filter(isLifestyleGroup);
  const formattedLifestyle =
    lifestyleGroups.length > 0
      ? lifestyleGroups.map((g) => ({
          ...g,
          groupName:
            g.groupName || "Lifestyle, Barriers and Functional Assessment",
        }))
      : [
          {
            id: "lifestyle_default",
            groupName: "Lifestyle, Barriers and Functional Assessment",
            programId: "LIFESTYLE_BARRIERS_FUNCTIONAL",
            groupVersion: 1,
            status: "ACTIVE",
            updatedAt: new Date().toISOString(),
          },
        ];

  // 4. Step 4: Care Plan, Coordination and Completion
  const carePlanGroups = list.filter(isCarePlanGroup);
  const formattedCarePlan =
    carePlanGroups.length > 0
      ? carePlanGroups.map((g) => ({
          ...g,
          groupName: g.groupName || "Care Plan, Coordination and Completion",
        }))
      : [
          {
            id: "careplan_default",
            groupName: "Care Plan, Coordination and Completion",
            programId: "CARE_PLAN_COORDINATION_COMPLETION",
            groupVersion: 1,
            status: "ACTIVE",
            updatedAt: new Date().toISOString(),
          },
        ];

  return [
    ...formattedGeneral,
    categoryHeader,
    ...programSubItems,
    ...formattedLifestyle,
    ...formattedCarePlan,
  ];
};

const getLogicalTemplateKey = (group = {}) =>
  [
    String(group.programId || "").toUpperCase(),
    String(group.groupType || "").toUpperCase(),
    (group.groupName || "").trim().toLowerCase(),
  ].join("|");

const renderBreadcrumbElement = (breadcrumb) => {
  if (breadcrumb.onClick) {
    return (
      <button
        type="button"
        style={{
          border: "none",
          background: "none",
          cursor: "pointer",
          padding: 0,
          color: "inherit",
          font: "inherit",
          textAlign: "left",
          textDecoration: "none",
        }}
        onClick={breadcrumb.onClick}
      >
        {breadcrumb.label}
      </button>
    );
  }
  if (breadcrumb.path) {
    return <Link to={breadcrumb.path}>{breadcrumb.label}</Link>;
  }
  return <span style={{ cursor: "default" }}>{breadcrumb.label}</span>;
};

const generateRandomKey = (prefix = "opt") => {
  const arr = new Uint32Array(1);
  globalThis.crypto.getRandomValues(arr);
  return `${prefix}-${arr[0]}`;
};

const getOptionKey = (option, index) => {
  if (!option) return `opt-${index}`;
  if (!option._key) {
    option._key =
      option.id ||
      option.label ||
      `opt-${index}-${Date.now()}-${generateRandomKey()}`;
  }
  return option._key;
};

const getFieldTypeLabel = (type) => {
  const typeMap = {
    single_select: "Single Choice",
    text: "Text Input",
    multi_select: "Multiple Choice",
    boolean: "Yes/No Toggle",
    lab_reference: "Lab Reference",
    vital_signs: "Vital Signs",
    cardiac_exam: "Cardiac Exam",
    lung_exam: "Lung Exam",
  };
  return typeMap[type] || type;
};

const FieldPaperItem = ({
  fieldKey,
  field,
  primaryColor,
  borderColor,
  handleFieldDragStart,
  handleFieldDragOver,
  onDragEndField,
  handleEditField,
  handleDeleteField,
}) => {
  const optionsCount = (field.options || []).length;
  return (
    <Paper
      draggable
      onDragStart={(e) => handleFieldDragStart(e, fieldKey)}
      onDragOver={(e) => handleFieldDragOver(e, fieldKey)}
      onDragEnd={onDragEndField}
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: "12px",
        border: `1px solid ${borderColor}`,
        boxShadow: "none",
        backgroundColor: "#fff",
        transition: "all 0.2s ease",
        "&:hover": {
          borderColor: primaryColor,
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              cursor: "grab",
              color: "#9CA3AF",
              display: "flex",
              alignItems: "center",
            }}
          >
            <GripVertical size={20} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 600,
                color: "#1F2937",
                fontSize: "15px",
              }}
            >
              {field.label}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                mt: 1,
              }}
            >
              <Chip
                label={getFieldTypeLabel(field.type)}
                size="small"
                sx={{
                  backgroundColor: "#F3F4F6",
                  color: "#374151",
                  fontWeight: 500,
                  fontSize: "11px",
                }}
              />
              {field.required && (
                <Chip
                  label="Required"
                  size="small"
                  sx={{
                    backgroundColor: "#FEF2F2",
                    color: "#EF4444",
                    fontWeight: 500,
                    fontSize: "11px",
                  }}
                />
              )}
              {optionsCount > 0 && (
                <Chip
                  label={`${optionsCount} Options`}
                  size="small"
                  sx={{
                    backgroundColor: "#EFF6FF",
                    color: "#3B82F6",
                    fontWeight: 500,
                    fontSize: "11px",
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Edit Question">
            <IconButton
              size="small"
              sx={{ color: primaryColor }}
              onClick={() => handleEditField(fieldKey, field)}
            >
              <Edit size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Question">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteField(fieldKey)}
            >
              <Trash2 size={18} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Paper>
  );
};

FieldPaperItem.propTypes = {
  fieldKey: PropTypes.string,
  field: PropTypes.shape({
    label: PropTypes.string,
    type: PropTypes.string,
    required: PropTypes.bool,
    options: PropTypes.array,
  }),
  primaryColor: PropTypes.string,
  borderColor: PropTypes.string,
  handleFieldDragStart: PropTypes.func,
  handleFieldDragOver: PropTypes.func,
  onDragEndField: PropTypes.func,
  handleEditField: PropTypes.func,
  handleDeleteField: PropTypes.func,
};

const resolveNextTemplate = async (
  type,
  group,
  cachedTemplates,
  isNewConfig,
  selectedProgram,
) => {
  let nextTemplate = cachedTemplates[type];
  if (nextTemplate) {
    return nextTemplate;
  }
  const normType = type.toUpperCase();
  const existingTemplate = group?.templates?.find(
    (t) => t.formType === normType,
  );

  if (existingTemplate) {
    const parsedPayload = parseSchemaPayload(existingTemplate.schemaPayload);
    return {
      ...existingTemplate,
      ...(parsedPayload || null),
      version: group.groupVersion,
    };
  }

  const defaultTemplate = await fetchAssessmentTemplateByType(type);
  const result = {
    formType: type.toUpperCase(),
    form: defaultTemplate.form
      ? {
          ...defaultTemplate.form,
          title: isNewConfig ? "" : defaultTemplate.form.title || "",
        }
      : { title: "", description: "" },
    sections: defaultTemplate.sections || {},
    version: group ? group.groupVersion : 0,
  };
  if (isNewConfig) {
    result.templateName = "";
    result.name = "";
    result.programId = selectedProgram
      ? selectedProgram.toUpperCase()
      : defaultTemplate.programId || "GLOBAL";
  }
  return result;
};

const TemplateNameCell = (info) => {
  const row = info.row.original;
  if (row.isCategoryHeader) {
    return (
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "15px",
          color: "#1E1B4B",
          letterSpacing: "0.5px",
          py: 0.5,
        }}
      >
        {row.groupName}
      </Typography>
    );
  }

  const name = row.groupName || getTemplateDisplayName(row) || "";
  const displayValue = name.length > 38 ? `${name.substring(0, 38)}...` : name;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        pl: row.isProgramSubItem ? 3 : 0,
      }}
    >
      {row.isProgramSubItem && (
        <CornerDownRight
          size={15}
          color="#9CA3AF"
          style={{ marginRight: 4, flexShrink: 0 }}
        />
      )}
      <Tooltip title={name} arrow placement="top-start">
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            fontSize: "15px",
            color: "#403C6F",
            cursor: "default",
          }}
        >
          {displayValue}
        </Typography>
      </Tooltip>
    </Box>
  );
};

const VersionCell = (info) => {
  if (info.row.original.isCategoryHeader) return null;
  const val = info.getValue();
  const label = `v${val === undefined ? "1.0.0" : val}`;
  return (
    <Tooltip title={`Version: ${label}`} arrow placement="top">
      <Chip
        label={label}
        size="small"
        sx={{
          height: "22px",
          fontSize: "11px",
          fontWeight: 700,
          backgroundColor: "#F0F0F5",
          color: "#403C6F",
          borderRadius: "4px",
          cursor: "default",
        }}
      />
    </Tooltip>
  );
};

const StatusCell = (info) => {
  if (info.row.original.isCategoryHeader) return null;
  const status = info.getValue() || "ACTIVE";
  const isActive = status === "ACTIVE";
  return (
    <Tooltip title={`Status: ${status}`} arrow placement="top">
      <Chip
        label={status}
        size="small"
        sx={{
          height: "22px",
          fontSize: "12px",
          fontWeight: 700,
          backgroundColor: isActive ? "#E6F4EA" : "#F3F4F6",
          color: isActive ? "#1E7E34" : "#6B7280",
          borderRadius: "4px",
          cursor: "default",
        }}
      />
    </Tooltip>
  );
};

const LastUpdatedCell = (info) => {
  if (info.row.original.isCategoryHeader) return null;
  const val = info.getValue();
  let displayDate = "N/A";
  if (val) {
    const d = new Date(val);
    if (!Number.isNaN(d.getTime())) {
      const datePart = d.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      });
      const timePart = d.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
      displayDate = `${datePart} ${timePart}`;
    }
  }

  return (
    <Tooltip
      title={displayDate === "N/A" ? "" : `Last Updated: ${displayDate}`}
      arrow
      placement="top"
    >
      <Typography
        variant="body2"
        sx={{ color: "#717171", fontSize: "14px", fontWeight: 500 }}
      >
        {displayDate}
      </Typography>
    </Tooltip>
  );
};

const getColumns = (draftTemplateKeys, handleFetchDetail) => [
  {
    header: createHeaderWithSort("Template Name", true),
    accessorKey: "groupName",
    cell: TemplateNameCell,
  },
  {
    header: createHeaderWithSort("Version", true),
    accessorKey: "groupVersion",
    cell: VersionCell,
  },
  {
    header: createHeaderWithSort("Status", true),
    accessorKey: "status",
    cell: StatusCell,
  },
  {
    header: createHeaderWithSort("Last Updated", true),
    accessorKey: "updatedAt",
    cell: LastUpdatedCell,
  },
  {
    header: createHeaderWithSort("Actions", false),
    id: "actions",
    cell: (info) => {
      const row = info.row.original;
      if (row.isCategoryHeader) return null;
      const rowStatus = String(row.status || "ACTIVE").toUpperCase();
      const isEditDisabled =
        rowStatus !== "DRAFT" &&
        draftTemplateKeys.has(getLogicalTemplateKey(row));
      return (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Review Template" arrow placement="top">
            <IconButton
              size="small"
              sx={{ color: "#717171" }}
              aria-label="Review Template"
              onClick={(e) => {
                e.stopPropagation();
                handleFetchDetail(row.id, true);
              }}
            >
              <Eye size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              isEditDisabled
                ? "A draft exists — edit the draft version instead"
                : "Edit Configuration"
            }
            arrow
            placement="top"
          >
            <span style={{ display: "inline-flex" }}>
              <IconButton
                size="small"
                disabled={isEditDisabled}
                aria-label="Edit Configuration"
                sx={{ color: "#403C6F" }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleFetchDetail(row.id);
                }}
              >
                <Edit size={18} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      );
    },
  },
];

const AssessmentConfigPage = () => {
  const navigate = useNavigate();
  const { templateId } = useParams(); // To handle direct navigation to editor
  const { showLoading, hideLoading } = useLoading();

  const [view, setView] = useState("list"); // "list" or "editor"
  const [templates, setTemplates] = useState([]);
  const [isNewConfig, setIsNewConfig] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [pendingChange, setPendingChange] = useState(null); // { type: 'type' | 'program', value: string }
  const [isConfirmUnsavedOpen, setIsConfirmUnsavedOpen] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [selectedType, setSelectedType] = useState("initial");
  const [selectedProgram, setSelectedProgram] = useState("general");
  const [selectedSectionKey, setSelectedSectionKey] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isInlineAdding, setIsInlineAdding] = useState(false);
  const [inlineSectionValue, setInlineSectionValue] = useState("");
  const [editingSectionKey, setEditingSectionKey] = useState(null);
  const [editSectionValue, setEditSectionValue] = useState("");
  const isSavingRef = useRef(false);
  const [draggedSectionKey, setDraggedSectionKey] = useState(null);
  const [draggedFieldKey, setDraggedFieldKey] = useState(null);
  const [draggedOptionIndex, setDraggedOptionIndex] = useState(null);
  const [sectionMenuAnchor, setSectionMenuAnchor] = useState(null);
  const [sectionMenuKey, setSectionMenuKey] = useState(null);
  const [isDeleteSectionConfirmOpen, setIsDeleteSectionConfirmOpen] =
    useState(false);
  const [sectionToDelete, setSectionToDelete] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [editorPreviewTemplate, setEditorPreviewTemplate] = useState(null);
  const [isListPreviewOpen, setIsListPreviewOpen] = useState(false);
  const [activeGroup, setActiveGroup] = useState(null);
  const [templatesCache, setTemplatesCache] = useState({});
  const [cachedGroupId, setCachedGroupId] = useState(null);
  const [programsList, setProgramsList] = useState([]);
  const [assessmentTypesList, setAssessmentTypesList] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const pristineSectionsRef = useRef(null);

  useEffect(() => {
    if (!isDirty && currentTemplate) {
      pristineSectionsRef.current = JSON.stringify(
        currentTemplate?.sections || {},
      );
    }
  }, [isDirty, currentTemplate]);

  useEffect(() => {
    const getRefData = async () => {
      try {
        const data = await fetchAssessmentReferenceData();
        if (data?.programs && Array.isArray(data.programs)) {
          setProgramsList(data.programs);
        }
        if (data?.assessmentTypes && Array.isArray(data.assessmentTypes)) {
          setAssessmentTypesList(data.assessmentTypes);
        }
      } catch (err) {
        console.error(
          "Failed to fetch assessment reference data in AssessmentConfigPage:",
          err,
        );
      }
    };
    void getRefData();
  }, []);

  const isGeneral =
    selectedProgram === "general" ||
    selectedProgram === "other" ||
    activeGroup?.groupType === "GENERAL";

  const primaryColor = "#403C6F";
  const borderColor = "#E8E8E9";

  useEffect(() => {
    const loadData = async () => {
      showLoading();
      try {
        // Optimization: If the group is already loaded (e.g. after a save), skip re-fetching
        const isAlreadyLoaded =
          activeGroup?.id?.toString() === templateId?.toString();

        if (templateId === "new") {
          setIsNewConfig(true);
          setActiveGroup(null);
          const defaultTemplate =
            await fetchAssessmentTemplateByType("initial");
          const initialTemplate = {
            templateName: "",
            programId: "",
            formType: "INITIAL",
            schemaVersion: "1.0.0",
            form: defaultTemplate.form
              ? { ...defaultTemplate.form, title: "" }
              : { title: "", description: "" },
            sections: defaultTemplate.sections || {},
            version: 0,
          };
          setTemplatesCache({
            initial: initialTemplate,
          });
          setCurrentTemplate(initialTemplate);
          setSelectedType("initial");
          setSelectedProgram("");
          if (
            initialTemplate.sections &&
            Object.keys(initialTemplate.sections).length > 0
          ) {
            setSelectedSectionKey(Object.keys(initialTemplate.sections)[0]);
          } else {
            setSelectedSectionKey(null);
          }
          setView("editor");
          setIsDirty(false);
        } else if (templateId && !isAlreadyLoaded) {
          await handleFetchDetail(templateId);
        } else if (!templateId) {
          // Only fetch the list of groups when at the dashboard view.
          // Show the latest version of every group — both active and draft
          // (a draft is preferred when it's the latest for that group).
          const listData = await fetchLatestAssessmentGroups();
          setTemplates(formatTemplatesForDisplay(listData));
          setView("list");
        }
      } catch (error) {
        console.error("Failed to initialize assessment config:", error);
      } finally {
        hideLoading();
      }
    };
    void loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  const sections = currentTemplate?.sections || {};
  const currentSection = sections[selectedSectionKey];

  // When the loaded template is a draft, the primary action publishes it.
  const isDraftTemplate =
    String(activeGroup?.status || "").toUpperCase() === "DRAFT";

  // A template can appear twice in the list: its ACTIVE record and a newer DRAFT
  // record. These are separate backend records (different ids) for the same
  // logical template (same program/type/name). When a draft exists, edits should
  // only be made on the draft, so we disable the edit action on the active row.

  const draftTemplateKeys = useMemo(() => {
    const keys = new Set();
    (Array.isArray(templates) ? templates : []).forEach((group) => {
      if (String(group?.status || "").toUpperCase() === "DRAFT") {
        keys.add(getLogicalTemplateKey(group));
      }
    });
    return keys;
  }, [templates]);

  const isVisibleForSelectedType = (item = {}) => true;

  const visibleSectionEntries = Object.entries(sections).filter(([, section]) =>
    isVisibleForSelectedType(section),
  );

  const getVisibleFields = (section = {}) =>
    Object.entries(section?.fields || {}).filter(([, field]) =>
      isVisibleForSelectedType(field),
    );

  const visibleCurrentSection =
    currentSection && isVisibleForSelectedType(currentSection)
      ? currentSection
      : null;

  useEffect(() => {
    const visibleKeys = visibleSectionEntries.map(([key]) => key);
    if (!selectedSectionKey || !visibleKeys.includes(selectedSectionKey)) {
      setSelectedSectionKey(visibleKeys.length > 0 ? visibleKeys[0] : null);
    }
  }, [
    selectedType,
    currentTemplate,
    selectedSectionKey,
    visibleSectionEntries,
  ]);

  const handleSaveNewConfig = async (updatedCache, isDraft) => {
    // Validate all templates in the cache only for final save/publish
    if (!isDraft) {
      for (const [, temp] of Object.entries(updatedCache)) {
        const payload = buildDefaultPayload(temp);
        const validation = await validateAssessmentGroupSchema(payload);
        if (!validation.valid) {
          return null;
        }
      }
    }

    const templatesPayload = Object.entries(updatedCache).map(
      ([type, temp]) => {
        const payload = buildDefaultPayload(temp);
        const sections = temp.sections || {};
        const sectionEntries = Object.entries(sections);
        const firstSection =
          sectionEntries.length > 0 ? sectionEntries[0][1] : null;
        const sectionName = firstSection?.label || "General";

        return {
          formType: type.toUpperCase(),
          sectionName: sectionName,
          sectionOrder: 1,
          schemaPayload: payload,
        };
      },
    );

    const groupPayload = {
      groupName: isGeneral
        ? currentTemplate.name ||
          currentTemplate.templateName ||
          activeGroup?.groupName ||
          "Core Assessment"
        : getProgramDisplayName(selectedProgram),
      groupType: isGeneral ? "GENERAL" : "PROGRAM_SPECIFIC",
      programId: isGeneral
        ? "OTHER"
        : selectedProgram.toUpperCase() || "GLOBAL",
      templates: templatesPayload,
    };
    const response = await createAssessmentGroup(groupPayload, isDraft);
    setActiveGroup(response);
    setIsNewConfig(false);
    return response;
  };

  const handleSaveExistingConfig = async (isDraft) => {
    // Build payload first
    const payload = buildDefaultPayload(currentTemplate);

    // Validate ONLY for final save/publish, not drafts
    if (!isDraft) {
      const validation = await validateAssessmentGroupSchema(payload);
      if (!validation.valid) {
        return null;
      }
    }

    // Call update API for the selected template
    const sections = currentTemplate.sections || {};
    const sectionEntries = Object.entries(sections);
    const firstSection =
      sectionEntries.length > 0 ? sectionEntries[0][1] : null;
    const sectionName = firstSection?.label || "General";

    const templatePayload = {
      formType: selectedType.toUpperCase(),
      sectionName: sectionName,
      sectionOrder: 1,
      schemaPayload: payload,
    };

    const expectedVersion = activeGroup?.groupVersion;

    const updateResponse = await updateAssessmentGroup(
      activeGroup.id,
      selectedType,
      [templatePayload],
      expectedVersion,
      isDraft,
      currentTemplate?.name ||
        currentTemplate?.form?.title ||
        activeGroup?.groupName,
      currentTemplate?.id,
    );
    return updateResponse;
  };

  const handleAutosaveStateUpdate = (response, updatedCache, isDraft) => {
    setTemplatesCache(updatedCache);
    const updateResponse = response;
    if (updateResponse) {
      const draftId = updateResponse.id ?? activeGroup?.id;
      const draftVersion = updateResponse.groupVersion;
      const status =
        updateResponse.status ?? (isDraft ? "DRAFT" : activeGroup?.status);
      setCachedGroupId(draftId || cachedGroupId);
      setActiveGroup((prev) =>
        prev
          ? {
              ...prev,
              id: draftId,
              status: status,
              ...(draftVersion === undefined
                ? {}
                : { groupVersion: draftVersion }),
              ...(updateResponse.groupName
                ? { groupName: updateResponse.groupName }
                : {}),
            }
          : prev,
      );

      // Sync template IDs, versions, and configurations from the backend response
      if (updateResponse.templates && Array.isArray(updateResponse.templates)) {
        const activeTypeNorm = selectedType ? selectedType.toUpperCase() : "";
        const matched = updateResponse.templates.find(
          (t) => t.formType === activeTypeNorm,
        );
        if (matched) {
          setCurrentTemplate((prev) =>
            prev
              ? {
                  ...prev,
                  id: matched.id,
                  version:
                    draftVersion === undefined ? prev.version : draftVersion,
                }
              : prev,
          );
        }

        setTemplatesCache((prev) => {
          const nextCache = { ...prev };
          updateResponse.templates.forEach((t) => {
            const typeKey = t.formType;
            if (typeKey) {
              const parsed = parseSchemaPayload(t.schemaPayload);
              nextCache[typeKey] = {
                ...t,
                ...parsed,
                version: draftVersion === undefined ? t.version : draftVersion,
              };
            }
          });
          return nextCache;
        });
      } else if (draftVersion !== undefined) {
        setCurrentTemplate((prev) =>
          prev ? { ...prev, version: draftVersion } : prev,
        );
        setTemplatesCache((prev) =>
          selectedType
            ? {
                ...prev,
                [selectedType]: {
                  ...(prev[selectedType] || currentTemplate),
                  version: draftVersion,
                },
              }
            : prev,
        );
      }
    } else {
      setCachedGroupId(activeGroup?.id || cachedGroupId);
    }
  };

  const handleSave = useCallback(
    async (shouldNavigate = true, isDraft = false) => {
      if (isNewConfig && (!selectedProgram || !selectedType)) {
        return { success: false };
      }
      showLoading();
      try {
        const updatedCache = {
          ...templatesCache,
          [selectedType]: currentTemplate,
        };

        let response;
        let updatedGroup;
        if (isNewConfig) {
          updatedGroup = await handleSaveNewConfig(updatedCache, isDraft);
          if (!updatedGroup) {
            hideLoading();
            return { success: false };
          }
        } else {
          response = await handleSaveExistingConfig(isDraft);
          if (!response) {
            hideLoading();
            return { success: false };
          }
          updatedGroup = response;
        }

        // Only re-fetch and overwrite local state when navigating (explicit save)
        if (shouldNavigate === false) {
          handleAutosaveStateUpdate(response, updatedCache, isDraft);
        } else {
          // Re-fetch the updated group to sync local state (latest version,
          // including draft if it's the newest).
          updatedGroup = await fetchAssessmentGroupById(
            activeGroup?.id || updatedGroup.id,
            "DRAFT",
          );
          setActiveGroup(updatedGroup);

          const newCache = buildTemplatesCache(updatedGroup);
          setTemplatesCache(newCache);
          if (newCache[selectedType]) {
            setCurrentTemplate(newCache[selectedType]);
          }
        }

        setIsDirty(false);
        if (shouldNavigate === false) {
          // For autosave, also refresh the templates list so table has latest data
          await refreshTemplatesList(
            setTemplates,
            "Failed to refresh assessment groups after autosave:",
          );
        } else {
          setView("list");
          navigate("/cg-config");
          await refreshTemplatesList(
            setTemplates,
            "Failed to refresh assessment groups:",
          );
        }
        return { success: true, updatedGroup };
      } catch (error) {
        console.error("Save failed:", error);
        return { success: false };
      } finally {
        hideLoading();
      }
    },
    [
      isNewConfig,
      selectedProgram,
      selectedType,
      templatesCache,
      currentTemplate,
      isGeneral,
      activeGroup,
      navigate,
      cachedGroupId,
      setCachedGroupId,
      setTemplates,
      setView,
    ],
  );

  const handleSaveDraft = async () => {
    const result = await handleSave(false, true);
    if (result?.success) {
      try {
        lastSavedRef.current = JSON.stringify(currentTemplate || {});
      } catch (e) {
        console.warn("JSON stringify failed for last saved template:", e);
      }
      setSnackbar({
        open: true,
        message: "Draft saved successfully.",
        severity: "success",
      });
    } else {
      setSnackbar({
        open: true,
        message: "Failed to save draft. Please try again.",
        severity: "error",
      });
    }
  };

  const handleBranchOptionChange = (answer, subOptIdx, value) => {
    const newOpts = [
      ...(editingField.branches?.[answer]?.subQuestion?.options || []),
    ];
    newOpts[subOptIdx] = {
      ...newOpts[subOptIdx],
      label: value,
    };
    setEditingField((prev) => ({
      ...prev,
      branches: {
        ...prev.branches,
        [answer]: {
          subQuestion: {
            ...prev.branches?.[answer]?.subQuestion,
            options: newOpts,
          },
        },
      },
    }));
  };

  const handleBranchOptionDelete = (answer, subOptIdx) => {
    const newOpts = [
      ...(editingField.branches?.[answer]?.subQuestion?.options || []),
    ];
    newOpts.splice(subOptIdx, 1);
    setEditingField((prev) => ({
      ...prev,
      branches: {
        ...prev.branches,
        [answer]: {
          subQuestion: {
            ...prev.branches?.[answer]?.subQuestion,
            options: newOpts,
          },
        },
      },
    }));
  };

  const renderGroupFieldsHelper = (f, renderFieldFn) => {
    return Object.keys(f.fields || {}).map((groupFieldKey) => (
      <Box
        key={groupFieldKey}
        sx={{
          width: {
            xs: "100%",
            sm: "calc(50% - 8px)",
            md: "calc(25% - 12px)",
          },
        }}
      >
        <Typography
          sx={{
            fontSize: "13px",
            color: "#403C6F",
            fontWeight: 500,
            mb: 1,
            letterSpacing: "0.5px",
          }}
        >
          {f.fields[groupFieldKey].label}
        </Typography>
        {renderFieldFn(groupFieldKey, f.fields[groupFieldKey])}
      </Box>
    ));
  };

  const renderPreviewField = (fKey, f) => {
    if (!f) return null;
    switch (f.type) {
      case "text":
      case "textarea":
        return (
          <TextField
            fullWidth
            multiline={f.type === "textarea"}
            rows={f.type === "textarea" ? 3 : 1}
            placeholder={
              (f.label || "").length > 14
                ? "Enter..."
                : `Enter ${f.label || "value"}`
            }
            variant="outlined"
            size="small"
            disabled
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                backgroundColor: "#fff !important",
                "& .MuiOutlinedInput-input.Mui-disabled": {
                  backgroundColor: "#fff !important",
                },
                "& fieldset": {
                  borderColor: borderColor,
                },
              },
            }}
          />
        );
      case "number":
      case "time":
        return (
          <TextField
            fullWidth
            size="small"
            type={f.type}
            placeholder={
              f.placeholder ||
              ((f.label || "").length > 14
                ? "Enter..."
                : `Enter ${f.label || "value"}`)
            }
            disabled
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                backgroundColor: "#fff !important",
                "& .MuiOutlinedInput-input.Mui-disabled": {
                  backgroundColor: "#fff !important",
                },
                "& fieldset": {
                  borderColor: borderColor,
                },
              },
              "& .MuiInputBase-input::placeholder": {
                opacity: "1 !important",
                color: "#9CA3AF",
              },
            }}
          />
        );
      case "date":
        return (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              value={null}
              disabled
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  sx: {
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      backgroundColor: "#fff !important",
                      "& .MuiOutlinedInput-input.Mui-disabled": {
                        backgroundColor: "#fff !important",
                      },
                      "& fieldset": {
                        borderColor: borderColor,
                      },
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>
        );
      case "single_select":
      case "multi_select":
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              height: "40px",
              px: 1.5,
              borderRadius: "8px",
              border: `1px solid ${borderColor}`,
              backgroundColor: "#fff",
              cursor: "default",
            }}
          >
            <Typography variant="body2" sx={{ color: "#9F9DB6" }}>
              {(f.label || "option").length > 14
                ? "Select..."
                : `Select ${f.label || "option"}`}
            </Typography>
            <KeyboardArrowDownIcon
              sx={{
                color: "#6B7280",
                fontSize: "20px",
              }}
            />
          </Box>
        );
      case "boolean":
        return (
          <RadioGroup row value="">
            <FormControlLabel
              value="yes"
              control={
                <Radio
                  size="small"
                  disabled
                  sx={{ p: 0.5, color: borderColor }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: "#10100F" }}>
                  Yes
                </Typography>
              }
            />
            <FormControlLabel
              value="no"
              control={
                <Radio
                  size="small"
                  disabled
                  sx={{ p: 0.5, color: borderColor }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: "#10100F" }}>
                  No
                </Typography>
              }
            />
          </RadioGroup>
        );
      case "checkbox":
        return (
          <FormControlLabel
            sx={{ ml: -0.5 }}
            control={
              <Checkbox
                size="small"
                checked={false}
                disabled
                sx={{
                  p: 0.5,
                  color: borderColor,
                  "&.Mui-checked": {
                    color: "#403C6F !important",
                  },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: "#10100F" }}>
                Yes / Completed
              </Typography>
            }
          />
        );
      case "group":
        return (
          <Box
            sx={{
              p: 2,
              border: `1px solid ${borderColor}`,
              borderRadius: "8px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              {renderGroupFieldsHelper(f, renderPreviewField)}
            </Box>
          </Box>
        );
      default:
        return null;
    }
  };

  const renderMainFieldsArea = () => {
    if (visibleCurrentSection) {
      return (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 4,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 700,
                  color: "#1b2f3f",
                }}
              >
                Questions
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#6B7280",
                  mt: 0.5,
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                }}
              >
                {visibleCurrentSection.label}
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<Plus size={18} />}
              onClick={handleAddField}
              sx={{
                backgroundColor: primaryColor,
                color: "#FFF",
                flexShrink: 0,
                "& svg": {
                  stroke: "#FFF !important",
                  color: "#FFF !important",
                },
                borderRadius: "50px",
                textTransform: "none",
                fontSize: "14px",
                fontWeight: 600,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#3a3458",
                  boxShadow: "none",
                },
              }}
            >
              Add Question
            </Button>
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              maxHeight: "calc(100vh - 350px)",
              overflowY: "auto",
              pr: 1,
              mr: -1,
              "&::-webkit-scrollbar": { width: "4px" },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#ECECF1",
                borderRadius: "4px",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                pb: 4,
              }}
            >
              {getVisibleFields(visibleCurrentSection).map(
                ([fieldKey, field]) => (
                  <FieldPaperItem
                    key={fieldKey}
                    fieldKey={fieldKey}
                    field={field}
                    primaryColor={primaryColor}
                    borderColor={borderColor}
                    handleFieldDragStart={handleFieldDragStart}
                    handleFieldDragOver={handleFieldDragOver}
                    onDragEndField={() => setDraggedFieldKey(null)}
                    handleEditField={handleEditField}
                    handleDeleteField={handleDeleteField}
                  />
                ),
              )}
              {getVisibleFields(visibleCurrentSection).length === 0 && (
                <Box
                  sx={{
                    py: 12,
                    textAlign: "center",
                    color: "text.secondary",
                    backgroundColor: "#F9FAFB",
                    borderRadius: "12px",
                    border: `2px dashed ${borderColor}`,
                  }}
                >
                  <Typography fontWeight={500}>
                    No questions in this section yet.
                  </Typography>
                </Box>
              )}

              {(selectedProgram === "care_plan_coordination_completion" ||
                currentTemplate?.programId ===
                  "CARE_PLAN_COORDINATION_COMPLETION" ||
                activeGroup?.programId ===
                  "CARE_PLAN_COORDINATION_COMPLETION") && (
                <Box
                  sx={{
                    mt: 3,
                    p: 2.5,
                    borderRadius: "12px",
                    backgroundColor: "#F8FAFC",
                    border: "1px dashed #CBD5E1",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "14px",
                      color: "#334155",
                      mb: 0.5,
                    }}
                  >
                    System-Generated Section: Signature & Completion
                  </Typography>
                  <Typography
                    sx={{ fontSize: "13px", color: "#64748B", mb: 1.5 }}
                  >
                    Clinical notes, Clinician Signature & credentials, and
                    completion details are automatically added during assessment
                    execution. These fields are system-managed and cannot be
                    removed, edited, or reordered in the template configuration.
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {[
                      "Clinical Notes",
                      "Clinician Signature & Credentials",
                      "Completion Details",
                    ].map((item) => (
                      <Chip
                        key={item}
                        label={item}
                        size="small"
                        variant="outlined"
                        sx={{
                          backgroundColor: "#FFFFFF",
                          borderColor: "#CBD5E1",
                          fontSize: "11px",
                          fontWeight: 600,
                          color: "#475569",
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </>
      );
    }

    if (visibleSectionEntries.length === 0) {
      return (
        <Box sx={{ py: 15, textAlign: "center", color: "text.secondary" }}>
          <Typography
            sx={{ fontSize: "16px", fontWeight: 700, color: "#1b2f3f" }}
          >
            No sections exist yet.
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 500,
              color: "#2D2D2D",
              mt: 1,
            }}
          >
            Add a new section from the sidebar to start configuring questions.
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ py: 15, textAlign: "center", color: "text.secondary" }}>
        <Typography
          sx={{ fontSize: "16px", fontWeight: 700, color: "#1b2f3f" }}
        >
          Select a section to configure its questions.
        </Typography>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 500,
            color: "#2D2D2D",
            mt: 1,
          }}
        >
          Choose from the sidebar on the left to start editing.
        </Typography>
      </Box>
    );
  };

  const lastSavedRef = useRef(null);

  // Auto-save: debounce changes and save in background without navigating
  useEffect(() => {
    if (!currentTemplate) return;
    const AUTO_SAVE_DELAY = 2 * 60 * 1000; // 2 minutes
    let timer = null;

    const attemptSave = async () => {
      if (!isDirty) return;
      // avoid saving if nothing changed since last successful save
      try {
        const last = lastSavedRef.current;
        const curr = JSON.stringify(currentTemplate || {});
        if (last === curr) {
          // nothing changed since last save
          setIsDirty(false);
          return;
        }
      } catch (e) {
        console.warn("JSON stringify failed for template comparison:", e);
      }
      if (isSavingRef.current) return;
      isSavingRef.current = true;
      try {
        const result = await handleSave(false, true);
        if (result?.success) {
          try {
            lastSavedRef.current = JSON.stringify(currentTemplate || {});
          } catch (e) {
            console.warn("JSON stringify failed for last saved template:", e);
          }
        }
      } catch (e) {
        console.error("Auto-save failed:", e);
      } finally {
        isSavingRef.current = false;
      }
    };

    timer = setTimeout(() => {
      void attemptSave();
    }, AUTO_SAVE_DELAY);

    const handleBeforeUnload = (e) => {
      if (!isDirty) return;
      // If the template already exists, persist the current work as a draft via
      // a keepalive request so it survives the reload — no warning needed.
      if (activeGroup?.id && selectedType) {
        try {
          const payload = buildDefaultPayload(currentTemplate);
          const sectionEntries = Object.entries(
            currentTemplate?.sections || {},
          );
          const sectionName =
            sectionEntries.length > 0
              ? sectionEntries[0][1]?.label || "General"
              : "General";
          void saveAssessmentDraftBeacon(
            activeGroup?.id,
            selectedType,
            [
              {
                formType: selectedType.toUpperCase(),
                sectionName,
                sectionOrder: 1,
                schemaPayload: payload,
              },
            ],
            activeGroup?.groupVersion,
            currentTemplate?.name ||
              currentTemplate?.form?.title ||
              activeGroup?.groupName,
            currentTemplate?.id,
          );
          return;
        } catch (err) {
          console.warn("saveAssessmentDraftBeacon failed:", err);
        }
      }
      // New, never-saved template (no group id yet) — warn so work isn't lost.
      e.preventDefault();
      e.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
    // only watch isDirty and currentTemplate to trigger autosave
  }, [isDirty, currentTemplate, handleSave, activeGroup, selectedType]);

  const getProgramDisplayName = useCallback(
    (prog) => {
      if (!prog) return "";
      const raw = String(prog).trim();
      const norm = raw.toUpperCase();
      if (
        norm === "GENERAL" ||
        norm === "GLOBAL" ||
        norm === "OTHER" ||
        norm === "OTHER_GENERAL"
      ) {
        return "Core Assessment";
      }
      const found = programsList.find(
        (p) =>
          String(p.programCode || "").toUpperCase() === norm ||
          String(p.displayName || "").toUpperCase() === norm ||
          String(p.name || "").toUpperCase() === norm ||
          (norm === "WOMENS_HEALTH" &&
            String(p.programCode || "").toUpperCase() ===
              "WOMENS_HEALTH_PROGRAM"),
      );
      if (found) {
        return found.displayName || found.name || "";
      }
      return "";
    },
    [programsList],
  );

  const getAssessmentTypeDisplayName = useCallback(
    (typeVal) => {
      if (!typeVal) return "";
      const raw = String(typeVal).trim();
      const norm = raw.toUpperCase();

      const foundInApi = assessmentTypesList.find(
        (t) => String(t).toUpperCase() === norm,
      );

      if (!foundInApi) return "";

      const typeKey = String(foundInApi).toUpperCase();

      if (typeKey === "INITIAL") return "Initial Assessment";
      if (typeKey === "FOLLOW_UP" || typeKey === "FOLLOWUP")
        return "Follow-up Assessment";
      if (typeKey === "DISCHARGE") return "Discharge Assessment";

      return (
        typeKey
          .replaceAll("_", " ")
          .toLowerCase()
          .replaceAll(/\b\w/g, (c) => c.toUpperCase()) + " Assessment"
      );
    },
    [assessmentTypesList],
  );

  const updateCurrentTemplate = (newCurrent, group = activeGroup) => {
    setCurrentTemplate(newCurrent);
    if (selectedType) {
      setTemplatesCache((prev) => ({
        ...prev,
        [selectedType]: {
          ...newCurrent,
          version:
            group?.groupVersion === undefined
              ? newCurrent.version
              : group.groupVersion,
        },
      }));
    }
    setIsDirty(true);
  };

  const getBreadcrumbs = () => {
    if (view === "editor") {
      const rawName =
        activeGroup?.groupName ||
        currentTemplate?.name ||
        currentTemplate?.form?.title ||
        currentTemplate?.templateName ||
        "";

      let templateName = rawName.trim();
      if (!templateName && !isNewConfig) {
        templateName = getTemplateDisplayName(currentTemplate);
      }

      const breadcrumbs = [
        {
          label: "CG+ Assessment Templates",
          onClick: () => {
            setView("list");
            navigate("/cg-config");
          },
        },
      ];

      if (templateName?.trim()) {
        breadcrumbs.push({ label: templateName.trim(), path: "" });
      }

      return breadcrumbs;
    }
    return [{ label: "CG+ Assessment Templates", path: "" }];
  };

  const modalInputStyle = {
    "& .MuiOutlinedInput-root": {
      height: "45px",
      borderRadius: "8px",
      fontSize: "14px",
      fontWeight: 500,
      backgroundColor: "#ffffff",
      "& fieldset": { borderColor: "#E0E0E0" },
      "&:hover fieldset": { borderColor: "#BDBDBD" },
      "&.Mui-focused fieldset": { borderColor: primaryColor },
    },
    "& .MuiInputBase-input::placeholder": {
      color: "#403c6f",
      opacity: 0.5,
      fontWeight: 500,
      fontSize: "13px",
    },
  };

  const modalSelectStyle = {
    borderRadius: "8px",
    height: "45px",
    fontSize: "14px",
    fontWeight: 500,
    backgroundColor: "#ffffff",
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E0E0E0" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#BDBDBD" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: primaryColor,
    },
  };

  const handleFetchDetail = async (id, isPreview = false) => {
    showLoading();
    try {
      // capture previous opened group id BEFORE updating state so we can
      // correctly detect whether we're re-opening the same group and thus
      // should merge local autosaved templates.
      const previousGroupId = (cachedGroupId || activeGroup?.id)?.toString();

      // Load the latest version, including a draft if it's the newest.
      const group = await fetchAssessmentGroupById(id, "DRAFT");
      setActiveGroup(group);
      setCachedGroupId(group.id);

      const newCache = {};
      group.templates?.forEach((t) => {
        const parsedPayload = parseSchemaPayload(t.schemaPayload);
        newCache[t.formType.toLowerCase()] = {
          ...t,
          ...(parsedPayload || null),
          version: group.groupVersion,
          programId: group.programId,
          groupName: group.groupName,
          groupType: group.groupType,
        };
      });

      // Merge with any locally autosaved templates only when the same group is reopened.
      const mergedCache = { ...newCache };
      const isSameGroup = previousGroupId === id?.toString();
      if (isSameGroup) {
        Object.keys(templatesCache || {}).forEach((k) => {
          if (templatesCache[k]) {
            mergedCache[k] = {
              ...(mergedCache[k] || null),
              ...templatesCache[k],
            };
          }
        });
      }

      setTemplatesCache(mergedCache);

      const formTypeToLoad = selectedType?.toUpperCase() || "INITIAL";
      // Prefer any locally autosaved templates (mergedCache) when deciding which
      // template/type to load so previews show autosaved new items and retain
      // the last selected assessment type when returning to the preview.
      const matchedTemplate =
        mergedCache[formTypeToLoad.toLowerCase()] ||
        Object.values(mergedCache)[0] ||
        Object.values(newCache)[0];
      const activeType = matchedTemplate
        ? (matchedTemplate.formType || "INITIAL").toLowerCase()
        : "initial";
      // Prefer any locally autosaved template for this group/type when previewing as well
      const localTemplateForPreview = mergedCache[activeType];
      if (isPreview) {
        const templateToPreview = localTemplateForPreview || matchedTemplate;
        setPreviewTemplate({
          ...templateToPreview,
          groupProgramId: group.programId,
          groupName: group.groupName,
        });
        setIsListPreviewOpen(true);
      } else {
        setIsNewConfig(false);
        setSelectedType(activeType);
        setSelectedProgram(group.programId || "");
        // If we have a locally autosaved template for this type, prefer it
        const localTemplate = mergedCache[activeType];
        setCurrentTemplate(
          localTemplate ||
            matchedTemplate || {
              formType: "INITIAL",
              form: { title: "", description: "" },
              sections: {},
              version: group.groupVersion,
            },
        );
        if (matchedTemplate?.sections) {
          setSelectedSectionKey(Object.keys(matchedTemplate.sections)[0]);
        }
        setView("editor");
        setIsDirty(false);
      }
    } catch (error) {
      console.error("Fetch detail failed:", error);
      if (!isPreview) {
        setView("list");
        navigate("/cg-config");
      }
    } finally {
      hideLoading();
    }
  };

  const columns = useMemo(
    () => getColumns(draftTemplateKeys, handleFetchDetail),
    [draftTemplateKeys, handleFetchDetail],
  );

  const handleCreateNew = () => {
    setIsNewConfig(true);
    setActiveGroup(null);
    setCachedGroupId(null);
    const initialTemplate = {
      templateName: "",
      programId: "",
      formType: "",
      schemaVersion: "",
      form: { title: "", description: "" },
      sections: {},
      version: 0,
    };
    setTemplatesCache({});
    setCurrentTemplate(initialTemplate);
    setSelectedType("");
    setSelectedProgram("");
    setSelectedSectionKey(null);
    setView("editor");
    setIsDirty(false);
  };

  if (view === "list") {
    return (
      <Box sx={{ p: 2, pt: 2, backgroundColor: "#F7F8FB", minHeight: "100vh" }}>
        {/* Breadcrumbs & Actions */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 0.5,
            marginBottom: "15px",
            minHeight: "40px",
            fontSize: "18px",
            fontWeight: 600,
            color: "#080A0E",
            "& a, & span:last-child": {
              color: "#080A0E",
              fontSize: "18px",
              fontWeight: 600,
              textDecoration: "none",
            },
            "& a:hover": { textDecoration: "underline" },
            "& .breadcrumb-separator": { margin: "0 4px", color: "#080A0E" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {getBreadcrumbs().map((breadcrumb, index) => (
              <React.Fragment key={breadcrumb.label || index}>
                {index > 0 && (
                  <span className="breadcrumb-separator">&gt;</span>
                )}
                {renderBreadcrumbElement(breadcrumb)}
              </React.Fragment>
            ))}
          </Box>

          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<Plus size={18} />}
              onClick={handleCreateNew}
              sx={{
                backgroundColor: primaryColor,
                color: "#FFF",
                "& svg": {
                  stroke: "#FFF !important",
                  color: "#FFF !important",
                },
                borderRadius: "8px",
                textTransform: "none",
                px: 3,
                height: "36px",
                fontWeight: 700,
                "&:hover": { backgroundColor: "#312e5e" },
              }}
            >
              Add Program Assessment
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 3 }}>
          {/* Assessment Table */}
          <Paper
            sx={{
              flex: 1,
              borderRadius: "12px",
              overflow: "hidden",
              border: `1px solid ${borderColor}`,
              boxShadow: "none",
            }}
          >
            <CommonTable
              columns={columns}
              data={templates}
              paginate={false}
              tableHeight="calc(100vh - 190px)"
            />
          </Paper>
        </Box>

        {/* List View Preview Modal */}
        <Dialog
          open={isListPreviewOpen}
          onClose={() => setIsListPreviewOpen(false)}
          maxWidth="lg"
          fullWidth
          slotProps={{
            paper: { sx: { borderRadius: "16px", minHeight: "80vh" } },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: `1px solid ${borderColor}`,
              pb: 2,
            }}
          >
            <Box>
              <Typography
                sx={{ fontSize: "20px", fontWeight: 600, color: "#1a1a1a" }}
              >
                Template Preview
              </Typography>
              <Typography variant="caption" color="textSecondary">
                This is how the assessment will appear to clinicians.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {activeGroup?.templates && activeGroup.templates.length >= 1 && (
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <Select
                    value={previewTemplate?.formType || ""}
                    onChange={(e) => {
                      const selectedTypeKey = e.target.value.toLowerCase();
                      const nextPreview = templatesCache[selectedTypeKey];
                      if (nextPreview) {
                        setPreviewTemplate({
                          ...nextPreview,
                          groupProgramId: activeGroup?.programId,
                          groupName: activeGroup?.groupName,
                        });
                      }
                    }}
                    sx={{
                      borderRadius: "8px",
                      fontSize: "14px",
                      backgroundColor: "#fff",
                    }}
                  >
                    {activeGroup.templates.map((t) => (
                      <MenuItem key={t.id} value={t.formType}>
                        {getAssessmentTypeDisplayName(t.formType)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <IconButton
                onClick={() => setIsListPreviewOpen(false)}
                size="small"
              >
                <Plus size={20} style={{ transform: "rotate(45deg)" }} />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent
            sx={{ p: 4, pt: "24px !important", backgroundColor: "#F7F8FB" }}
          >
            <Box sx={{ maxWidth: "900px", mx: "auto" }}>
              <Paper
                sx={{
                  p: 4,
                  borderRadius: "12px",
                  border: `1px solid ${borderColor}`,
                  boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "20px",
                    fontWeight: 600,
                    color: "#1a1a1a",
                    mb: 2,
                  }}
                >
                  {(() => {
                    const groupName =
                      previewTemplate?.groupName || activeGroup?.groupName;
                    if (groupName) return groupName;

                    const originalTitle = previewTemplate?.form?.title || "";
                    const cleanedTitle = originalTitle.replaceAll(
                      /\s{0,10}-\s{0,10}(?:(?:Initial|Follow-up|Discharge)\s{0,10})?Assessment/gi,
                      "",
                    );
                    if (
                      cleanedTitle &&
                      !cleanedTitle.includes("Care Management Program")
                    ) {
                      return cleanedTitle;
                    }

                    const progKey =
                      previewTemplate?.groupProgramId ||
                      previewTemplate?.programId ||
                      previewTemplate?.program ||
                      "general";
                    return getProgramDisplayName(progKey);
                  })()}
                </Typography>

                {Object.keys(previewTemplate?.sections || {}).map(
                  (sectionKey) => {
                    const section = previewTemplate.sections[sectionKey];
                    return (
                      <Box key={sectionKey} sx={{ mb: 5 }}>
                        <Box
                          sx={{
                            backgroundColor: "#ECECF1",
                            p: 1.5,
                            mb: 3,
                            borderRadius: "4px",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "16px",
                              fontWeight: 700,
                              color: "#1b2f3f",
                            }}
                          >
                            {section?.label}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 3,
                            px: 1,
                            alignItems: "stretch",
                          }}
                        >
                          {Object.keys(section?.fields || {}).map(
                            (fieldKey) => {
                              const field = section?.fields[fieldKey];
                              const isFullWidth =
                                field?.type === "textarea" ||
                                field?.type === "group" ||
                                fieldKey === "labs";

                              return (
                                <Box
                                  key={fieldKey}
                                  sx={{
                                    width: isFullWidth
                                      ? "100%"
                                      : {
                                          xs: "100%",
                                          sm: "calc(33.333% - 16px)",
                                        },
                                    mb: 1,
                                    display: "flex",
                                    flexDirection: "column",
                                    height: "100%",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "13px",
                                      color: "#403C6F",
                                      fontWeight: 500,
                                      mb: 1,
                                      display: "block",
                                      letterSpacing: "0.5px",
                                    }}
                                  >
                                    {field?.label}{" "}
                                    {field?.required && (
                                      <span style={{ color: "red" }}>*</span>
                                    )}
                                  </Typography>
                                  <Box sx={{ mt: "auto" }}>
                                    {renderPreviewField(fieldKey, field)}
                                  </Box>
                                </Box>
                              );
                            },
                          )}
                        </Box>
                      </Box>
                    );
                  },
                )}
              </Paper>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: `1px solid ${borderColor}` }}>
            <Button
              onClick={() => setIsListPreviewOpen(false)}
              variant="contained"
              sx={{ backgroundColor: primaryColor, px: 4, borderRadius: "8px" }}
            >
              Close Preview
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  const DEFAULT_FORM_TITLE = "Care Management Program - Initial Assessment";
  const DEFAULT_FORM_DESCRIPTION = // Default description for new templates
    "Structured RN assessment for symptom burden, medication support, risk identification, and care coordination";

  const contractSectionKeyMap = {};
  const contractFieldKeyMap = {};
  const contractGroupFieldKeyMap = { labs: {} };

  const toSnakeCase = (value = "") => {
    const s = value
      .toString()
      .trim()
      .toLowerCase()
      .replaceAll(/[^a-z0-9]+/g, "_");
    let start = 0;
    let end = s.length;
    while (start < end && s[start] === "_") start++;
    while (end > start && s[end - 1] === "_") end--;
    return s.slice(start, end);
  };

  const resolveStableKey = (
    rawKey,
    label,
    fallbackPrefix,
    usedKeys,
    mappedKey = "",
  ) => {
    const isTemporary =
      !rawKey || rawKey.startsWith("new_") || rawKey.startsWith("sub_field_");
    const preferred = isTemporary
      ? mappedKey || toSnakeCase(label)
      : mappedKey || toSnakeCase(rawKey) || rawKey;
    const baseKey = preferred || `${fallbackPrefix}_${usedKeys.size + 1}`;

    let nextKey = baseKey;
    let index = 2;
    while (usedKeys.has(nextKey)) {
      nextKey = `${baseKey}_${index}`;
      index += 1;
    }
    usedKeys.add(nextKey);
    return nextKey;
  };

  const normalizeFieldForPayload = (field = {}, parentFieldKey = "") => {
    const fieldType = field.type || "text";
    const normalized = {
      type: fieldType,
      label: field.label || "",
      required: !!field.required,
      visible_when: field.visible_when || null,
      branches: field.branches || null,
      isProgramSpecific: !!field.isProgramSpecific,
      specificPrograms: field.specificPrograms || [],
    };

    if (fieldType === "group") {
      const usedGroupKeys = new Set();
      const groupFields = Object.entries(field.fields || {}).reduce(
        (acc, [groupKey, groupField]) => {
          const mappedGroupKey =
            contractGroupFieldKeyMap[parentFieldKey]?.[
              (groupField?.label || "").trim().toLowerCase()
            ];
          const stableGroupKey = resolveStableKey(
            groupKey,
            groupField?.label,
            "group_field",
            usedGroupKeys,
            mappedGroupKey,
          );
          acc[stableGroupKey] = normalizeFieldForPayload(
            groupField,
            stableGroupKey,
          );
          return acc;
        },
        {},
      );
      return {
        ...normalized,
        fields: groupFields,
      };
    }

    if (fieldType === "single_select" || fieldType === "multi_select") {
      return {
        ...normalized,
        value: field.value === undefined ? null : field.value,
        options: (field.options || []).map((option) => ({
          label: option?.label || "",
          is_selected: Boolean(option?.is_selected),
        })),
      };
    }

    if (fieldType === "boolean" || fieldType === "checkbox") {
      return {
        ...normalized,
        is_checked: field.is_checked ?? null,
      };
    }

    if (fieldType === "list") {
      return {
        ...normalized,
        max_items: typeof field.max_items === "number" ? field.max_items : 3,
        value: Array.isArray(field.value) ? field.value : [],
      };
    }

    return {
      ...normalized,
      value: field.value ?? null,
    };
  };

  const buildDefaultPayload = (template = {}) => {
    const usedSectionKeys = new Set();
    const normalizedSections = Object.entries(template.sections || {}).reduce(
      (sectionAcc, [sectionKey, section]) => {
        const mappedSectionKey =
          contractSectionKeyMap[(section?.label || "").trim().toLowerCase()];
        const stableSectionKey = resolveStableKey(
          sectionKey,
          section?.label,
          "section",
          usedSectionKeys,
          mappedSectionKey,
        );
        const usedFieldKeys = new Set();
        const normalizedFields = Object.entries(section?.fields || {}).reduce(
          (fieldAcc, [fieldKey, field]) => {
            const mappedFieldKey =
              contractFieldKeyMap?.[stableSectionKey]?.[
                (field?.label || "").trim().toLowerCase()
              ];
            const stableFieldKey = resolveStableKey(
              fieldKey,
              field?.label,
              "field",
              usedFieldKeys,
              mappedFieldKey,
            );
            fieldAcc[stableFieldKey] = normalizeFieldForPayload(
              field,
              stableFieldKey,
            );
            return fieldAcc;
          },
          {},
        );

        sectionAcc[stableSectionKey] = {
          label: section?.label || "",
          fields: normalizedFields,
        };

        return sectionAcc;
      },
      {},
    );
    return {
      form: {
        title: template?.form?.title || template?.name || DEFAULT_FORM_TITLE,
        description: template?.form?.description || DEFAULT_FORM_DESCRIPTION,
      },
      sections: normalizedSections,
    };
  };

  const handleTypeChange = async (type, group = activeGroup) => {
    if (type === selectedType) return;

    showLoading();
    try {
      const cachedTemplates = {
        ...templatesCache,
      };
      if (selectedType) {
        cachedTemplates[selectedType] = currentTemplate;
      }
      setTemplatesCache(cachedTemplates);

      setSelectedType(type);

      const nextTemplate = await resolveNextTemplate(
        type,
        group,
        cachedTemplates,
        isNewConfig,
        selectedProgram,
      );

      if (!cachedTemplates[type]) {
        setTemplatesCache((prev) => ({
          ...prev,
          [type]: nextTemplate,
        }));
      }

      setCurrentTemplate(nextTemplate);
      pristineSectionsRef.current = JSON.stringify(
        nextTemplate?.sections || {},
      );

      if (
        nextTemplate.sections &&
        Object.keys(nextTemplate.sections).length > 0
      ) {
        setSelectedSectionKey(Object.keys(nextTemplate.sections)[0]);
      } else {
        setSelectedSectionKey(null);
      }
    } catch (error) {
      console.error("Failed to load template sections for type:", type, error);
    } finally {
      hideLoading();
    }
  };

  const executeProgramChange = (newProgram, group = activeGroup) => {
    setSelectedProgram(newProgram);
    updateCurrentTemplate(
      {
        ...currentTemplate,
        program: newProgram,
      },
      group,
    );
  };

  const handleTypeChangeWithCheck = (newType) => {
    if (newType === selectedType) return;
    const hasContent = Object.keys(currentTemplate?.sections || {}).length > 0;
    const currentSectionsStr = JSON.stringify(currentTemplate?.sections || {});
    const hasSectionsOrQuestionsChanged =
      pristineSectionsRef.current !== null &&
      currentSectionsStr !== pristineSectionsRef.current;

    if (
      isDirty &&
      hasSectionsOrQuestionsChanged &&
      (!isNewConfig || hasContent)
    ) {
      setPendingChange({ type: "type", value: newType });
      setIsConfirmUnsavedOpen(true);
    } else {
      void handleTypeChange(newType);
      if (isNewConfig && !hasContent) {
        setIsDirty(false);
      }
    }
  };

  const handleProgramChangeWithCheck = (newProgram) => {
    if (newProgram === selectedProgram) return;
    const hasContent = Object.keys(currentTemplate?.sections || {}).length > 0;
    const currentSectionsStr = JSON.stringify(currentTemplate?.sections || {});
    const hasSectionsOrQuestionsChanged =
      pristineSectionsRef.current !== null &&
      currentSectionsStr !== pristineSectionsRef.current;

    if (
      isDirty &&
      hasSectionsOrQuestionsChanged &&
      (!isNewConfig || hasContent)
    ) {
      setPendingChange({ type: "program", value: newProgram });
      setIsConfirmUnsavedOpen(true);
    } else {
      executeProgramChange(newProgram);
      if (isNewConfig && !hasContent) {
        setIsDirty(false);
      }
    }
  };

  const handleConfirmSave = async () => {
    const result = await handleSave(false, isDraftTemplate);
    const success = result?.success;
    const updatedGroup = result?.updatedGroup;
    if (success) {
      if (updatedGroup) {
        setActiveGroup(updatedGroup);
        if (updatedGroup.id) {
          setCachedGroupId(updatedGroup.id);
        }
      }
      if (pendingChange.type === "type") {
        await handleTypeChange(pendingChange.value, updatedGroup);
      } else if (pendingChange.type === "program") {
        executeProgramChange(pendingChange.value, updatedGroup);
      }
      setIsConfirmUnsavedOpen(false);
      setPendingChange(null);
    }
  };

  const handleConfirmCancel = () => {
    setIsConfirmUnsavedOpen(false);
    setPendingChange(null);
  };

  const handleAddField = () => {
    setEditingField({
      key: `new_field_${Date.now()}`,
      label: "",
      type: "text",
      assessmentType: selectedType,
      options: [],
      fields: {},
      isProgramSpecific: false,
      specificPrograms: [],
      branches: null, // New property for conditional branching (sub-questions)
      required: false, // Default to false
      visible_when: null, // Default to not conditionally visible
    });
    setIsEditModalOpen(true);
  };

  const handleEditField = (key, field) => {
    setEditingField({
      ...field,
      key,
      visible_when: field.visible_when || null, // Keep existing visible_when for other fields to depend on this one
      required: !!field.required,
      isProgramSpecific: !!field.isProgramSpecific,
      specificPrograms: field.specificPrograms || [],
      branches: field.branches || null, // Initialize branches
    });
    setIsEditModalOpen(true);
  };

  const handleDeleteField = (fieldKey) => {
    const updatedSections = { ...sections };
    delete updatedSections[selectedSectionKey].fields[fieldKey];
    updateCurrentTemplate({ ...currentTemplate, sections: updatedSections });
  };

  const handleSaveField = () => {
    const updatedSections = { ...sections };
    const { key, assessmentTypes, ...fieldData } = editingField;

    // Clean up visible_when if it's empty or not enabled (existing logic)
    const visibleWhenToSave =
      fieldData.visible_when?.key && fieldData.visible_when.value !== ""
        ? fieldData.visible_when
        : null;

    // Clean up branches if conditional branching is not enabled or no branches are defined
    const branchesToSave =
      Object.keys(fieldData.branches || {}).length > 0
        ? fieldData.branches
        : null;

    updatedSections[selectedSectionKey].fields[key] = {
      ...fieldData, // assessmentType is no longer a field property, it's template-level
      assessmentType: selectedType,
      visible_when: visibleWhenToSave,
      branches: branchesToSave, // Save branches
    };

    updateCurrentTemplate({ ...currentTemplate, sections: updatedSections });
    setIsEditModalOpen(false);
  };

  const handleSaveSectionEdit = (key, value) => {
    isSavingRef.current = true;
    const trimmed = value.trim();
    if (trimmed) {
      const updatedSections = { ...sections };
      updatedSections[key] = {
        ...updatedSections[key],
        label: trimmed,
      };
      updateCurrentTemplate({
        ...currentTemplate,
        sections: updatedSections,
      });
    }
    setEditingSectionKey(null);
    setEditSectionValue("");
    setTimeout(() => {
      isSavingRef.current = false;
    }, 100);
  };

  const handleAddSection = () => {
    setIsInlineAdding(true);
    setInlineSectionValue("");
  };

  const handleDeleteSection = () => {
    if (!sectionToDelete) return;
    const updatedSections = { ...sections };
    delete updatedSections[sectionToDelete];
    updateCurrentTemplate({ ...currentTemplate, sections: updatedSections });

    if (selectedSectionKey === sectionToDelete) {
      const remainingKeys = Object.keys(updatedSections);
      setSelectedSectionKey(remainingKeys.length > 0 ? remainingKeys[0] : null);
    }

    setIsDeleteSectionConfirmOpen(false);
    setSectionToDelete(null);
    setSectionMenuAnchor(null);
    setSectionMenuKey(null);
  };

  const handleSectionDragStart = (e, key) => {
    setDraggedSectionKey(key);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleSectionDragOver = (e, targetKey) => {
    e.preventDefault();
    if (!draggedSectionKey || draggedSectionKey === targetKey) return;

    const sectionKeys = Object.keys(sections);
    const draggedIndex = sectionKeys.indexOf(draggedSectionKey);
    const targetIndex = sectionKeys.indexOf(targetKey);

    if (draggedIndex === -1 || targetIndex === -1) return;

    const newSectionKeys = [...sectionKeys];
    newSectionKeys.splice(draggedIndex, 1);
    newSectionKeys.splice(targetIndex, 0, draggedSectionKey);

    const newSections = {};
    newSectionKeys.forEach((k) => {
      newSections[k] = sections[k];
    });

    updateCurrentTemplate({ ...currentTemplate, sections: newSections });
  };

  const handleFieldDragStart = (e, key) => {
    setDraggedFieldKey(key);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleFieldDragOver = (e, targetKey) => {
    e.preventDefault();
    if (!draggedFieldKey || draggedFieldKey === targetKey) return;

    const fieldKeys = Object.keys(currentSection.fields);
    const draggedIndex = fieldKeys.indexOf(draggedFieldKey);
    const targetIndex = fieldKeys.indexOf(targetKey);

    if (draggedIndex === -1 || targetIndex === -1) return;

    const newFieldKeys = [...fieldKeys];
    newFieldKeys.splice(draggedIndex, 1);
    newFieldKeys.splice(targetIndex, 0, draggedFieldKey);

    const newFields = {};
    newFieldKeys.forEach((k) => {
      newFields[k] = currentSection.fields[k];
    });

    const updatedSection = {
      ...currentSection,
      fields: newFields,
    };

    const updatedSections = {
      ...sections,
      [selectedSectionKey]: updatedSection,
    };

    updateCurrentTemplate({ ...currentTemplate, sections: updatedSections });
  };

  const handleFieldDropOnSection = (e, targetSectionKey) => {
    e.preventDefault();
    if (!draggedFieldKey) return;

    const sourceSectionKey = selectedSectionKey;
    if (sourceSectionKey === targetSectionKey) return;

    const sourceSection = sections[sourceSectionKey];
    const targetSection = sections[targetSectionKey];
    if (!sourceSection || !targetSection) return;

    const fieldToMove = sourceSection.fields[draggedFieldKey];
    if (!fieldToMove) return;

    // Remove from source, add to target
    const updatedSourceFields = { ...sourceSection.fields };
    delete updatedSourceFields[draggedFieldKey];

    const updatedTargetFields = {
      ...targetSection.fields,
      [draggedFieldKey]: fieldToMove,
    };

    const updatedSections = {
      ...sections,
      [sourceSectionKey]: {
        ...sourceSection,
        fields: updatedSourceFields,
      },
      [targetSectionKey]: {
        ...targetSection,
        fields: updatedTargetFields,
      },
    };

    updateCurrentTemplate({
      ...currentTemplate,
      sections: updatedSections,
    });

    // Select the target section to show the moved question
    setSelectedSectionKey(targetSectionKey);
    setDraggedFieldKey(null);
  };

  const handleOptionDragStart = (e, index) => {
    setDraggedOptionIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleOptionDragOver = (e, targetIndex) => {
    e.preventDefault();
    if (draggedOptionIndex === null || draggedOptionIndex === targetIndex)
      return;

    const newOptions = [...editingField.options];
    const draggedOption = newOptions[draggedOptionIndex];

    newOptions.splice(draggedOptionIndex, 1);
    newOptions.splice(targetIndex, 0, draggedOption);

    setEditingField({
      ...editingField,
      options: newOptions,
    });
    setDraggedOptionIndex(targetIndex);
  };

  return (
    <Box sx={{ p: 2, pt: 2, backgroundColor: "#F7F8FB", minHeight: "100vh" }}>
      {/* Breadcrumbs & Actions */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 0.5,
          marginBottom: "18px",
          minHeight: "40px",
          fontSize: "18px",
          fontWeight: 600,
          color: "#1a1a1a",
          "& a, & span:last-child": {
            color: "#1a1a1a",
            fontSize: "18px",
            fontWeight: 600,
            textDecoration: "none",
          },
          "& a:hover": { textDecoration: "underline" },
          "& .breadcrumb-separator": { margin: "0 4px", color: "#1a1a1a" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {getBreadcrumbs().map((breadcrumb, index) => (
            <React.Fragment key={breadcrumb.label || index}>
              {index > 0 && <span className="breadcrumb-separator">&gt;</span>}
              {renderBreadcrumbElement(breadcrumb)}
            </React.Fragment>
          ))}
        </Box>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Tooltip
            title={
              hasSectionsAndQuestions(currentTemplate)
                ? "Preview Template"
                : "No sections and questions created to preview"
            }
          >
            <span>
              <Button
                variant="outlined"
                startIcon={<Eye size={18} />}
                disabled={!hasSectionsAndQuestions(currentTemplate)}
                onClick={() => {
                  setEditorPreviewTemplate(currentTemplate);
                  setIsPreviewModalOpen(true);
                }}
                sx={{
                  color: primaryColor,
                  textTransform: "none",
                  border: "1px solid #E8E8E9",
                  borderColor: primaryColor,
                  borderRadius: "20px",
                  fontWeight: 400,
                  fontSize: "15px",
                  pt: 0.9,
                  pb: 0.9,
                  px: 2,
                  background: "#ffffff",
                  "&:hover": {
                    backgroundColor: "rgba(70, 64, 90, 0.04)",
                    borderColor: primaryColor,
                  },
                  "&.Mui-disabled": {
                    borderColor: "#E8E8E9",
                    color: "rgba(64, 60, 111, 0.4)",
                  },
                }}
              >
                Preview Template
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="outlined"
            onClick={handleSaveDraft}
            disabled={!isDirty}
            sx={{
              color: primaryColor,
              textTransform: "none",
              border: "1px solid #E8E8E9",
              borderColor: primaryColor,
              borderRadius: "50px",
              fontWeight: 600,
              fontSize: "14px",
              padding: "6px 16px",
              background: "#ffffff",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "rgba(70, 64, 90, 0.04)",
                borderColor: primaryColor,
              },
              "&.Mui-disabled": {
                borderColor: "rgba(64, 60, 111, 0.4)",
                color: "rgba(64, 60, 111, 0.4)",
              },
            }}
          >
            Save Draft
          </Button>
          <Button
            variant="contained"
            onClick={() => handleSave(true, false)}
            disabled={
              isDraftTemplate
                ? false
                : !isDirty || Object.keys(sections).length === 0
            }
            sx={{
              backgroundColor: primaryColor,
              borderRadius: "50px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "14px",
              padding: "6px 16px",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#4b3f72",
                boxShadow: "none",
              },
              "&.Mui-disabled": {
                backgroundColor: "rgba(64, 60, 111, 0.4)",
                color: "rgba(255, 255, 255, 0.7)",
              },
            }}
          >
            {isDraftTemplate ? "Publish" : "Save Template"}
          </Button>
        </Box>
      </Box>

      {/* Preview Modal */}
      <Dialog
        open={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        maxWidth="lg"
        fullWidth
        slotProps={{
          paper: { sx: { borderRadius: "16px", minHeight: "80vh" } },
        }}
      >
        {" "}
        {/* Preview Modal for Editor View */}
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: `1px solid ${borderColor}`,
            pb: 2,
          }}
        >
          <Box>
            <Typography
              sx={{ fontSize: "20px", fontWeight: 600, color: "#1a1a1a" }}
            >
              Template Preview
            </Typography>
            <Typography variant="caption" color="textSecondary">
              This is how the assessment will appear to clinicians.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {activeGroup?.templates && activeGroup.templates.length >= 1 && (
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <Select
                  value={editorPreviewTemplate?.formType || ""}
                  onChange={(e) => {
                    const selectedTypeKey = e.target.value.toLowerCase();
                    if (selectedTypeKey === selectedType?.toLowerCase()) {
                      setEditorPreviewTemplate(currentTemplate);
                    } else {
                      const nextPreview = templatesCache[selectedTypeKey];
                      if (nextPreview) {
                        setEditorPreviewTemplate({
                          ...nextPreview,
                          groupProgramId: activeGroup.programId,
                        });
                      }
                    }
                  }}
                  sx={{
                    borderRadius: "8px",
                    fontSize: "14px",
                    backgroundColor: "#fff",
                  }}
                >
                  {activeGroup.templates.map((t) => (
                    <MenuItem key={t.id} value={t.formType}>
                      {getAssessmentTypeDisplayName(t.formType)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <IconButton
              onClick={() => setIsPreviewModalOpen(false)}
              size="small"
            >
              <Plus size={20} style={{ transform: "rotate(45deg)" }} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{ p: 4, pt: "24px !important", backgroundColor: "#F7F8FB" }}
        >
          <Box sx={{ maxWidth: "900px", mx: "auto" }}>
            <Paper
              sx={{
                p: 4,
                borderRadius: "12px",
                border: `1px solid ${borderColor}`,
                boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
              }}
            >
              {/* <Typography
                sx={{
                  fontSize: "20px",
                  fontWeight: 600,
                  color: "#1a1a1a",
                  mb: 2,
                }}
              >
                {currentTemplate.form?.title}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 4 }}>
                {currentTemplate.form?.description}
              </Typography> */}

              {(() => {
                const previewSections = editorPreviewTemplate?.sections || {};
                const previewSectionEntries = Object.entries(previewSections);
                return previewSectionEntries.map(([sectionKey, section]) => {
                  return (
                    <Box key={sectionKey} sx={{ mb: 5 }}>
                      <Box
                        sx={{
                          backgroundColor: "#ECECF1",
                          p: 1.5,
                          mb: 3,
                          borderRadius: "4px",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "16px",
                            fontWeight: 700,
                            color: "#1b2f3f",
                          }}
                        >
                          {section?.label}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 3,
                          px: 1,
                          alignItems: "stretch",
                        }}
                      >
                        {getVisibleFields(section).map(([fieldKey, field]) => {
                          const isFullWidth =
                            field?.type === "textarea" ||
                            field?.type === "group" ||
                            fieldKey === "labs";

                          return (
                            <Box
                              key={fieldKey}
                              sx={{
                                width: isFullWidth
                                  ? "100%"
                                  : { xs: "100%", sm: "calc(33.333% - 16px)" },
                                mb: 1,
                                display: "flex",
                                flexDirection: "column",
                                height: "100%",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "13px",
                                  color: "#403C6F",
                                  fontWeight: 500,
                                  mb: 1,
                                  display: "block",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                {field?.label}{" "}
                                {field?.required && (
                                  <span style={{ color: "red" }}>*</span>
                                )}
                              </Typography>
                              <Box sx={{ mt: "auto" }}>
                                {renderPreviewField(fieldKey, field)}
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  );
                });
              })()}
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: `1px solid ${borderColor}` }}>
          <Button
            onClick={() => setIsPreviewModalOpen(false)}
            variant="contained"
            sx={{ backgroundColor: primaryColor, px: 4, borderRadius: "8px" }}
          >
            Close Preview
          </Button>
        </DialogActions>
      </Dialog>

      {/* Editor Configuration Header */}
      <Paper
        sx={{
          p: 2,
          mb: 2,
          borderRadius: "16px",
          border: `1px solid ${borderColor}`,
          boxShadow: "0 2px 12px rgba(0,0,0,0.02)",
          backgroundColor: "#fff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 4,
            alignItems: "center",
          }}
        >
          {/* Program Name Selector (for new templates) or static display (for editing program-specific templates) */}
          {(isNewConfig || !isGeneral) && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography
                sx={{ fontWeight: 500, color: "#2D2D2D", fontSize: "14px" }}
              >
                Program Name:
              </Typography>
              {isNewConfig ? (
                <FormControl size="small" sx={{ width: "250px" }}>
                  <Select
                    displayEmpty
                    value={selectedProgram}
                    onChange={(e) => {
                      handleProgramChangeWithCheck(e.target.value);
                    }}
                    sx={{
                      borderRadius: "10px",
                      fontSize: "14px",
                      fontWeight: 600,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#E8E8E9",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: primaryColor,
                      },
                    }}
                    renderValue={(selected) => {
                      if (!selected)
                        return (
                          <span style={{ color: "#9e9e9e" }}>
                            Select Program
                          </span>
                        );
                      return getProgramDisplayName(selected);
                    }}
                  >
                    {programsList
                      .filter(
                        (p) =>
                          p.status === true ||
                          p.status === 1 ||
                          p.status === undefined,
                      )
                      .filter((p) => {
                        const code = String(
                          p.programCode || p.id || "",
                        ).toUpperCase();
                        return (
                          code !== "BIOSIMILARS" &&
                          code !== "COPAY_ASSIST" &&
                          code !== "OTHER"
                        );
                      })
                      .map((p) => {
                        const progCode = p.programCode || p.id;
                        const isDisabled = templates.some(
                          (g) =>
                            (g.programId === progCode ||
                              g.programId === p.name ||
                              g.groupName === p.displayName) &&
                            g.status === "ACTIVE",
                        );
                        return (
                          <MenuItem
                            key={progCode || p.pk}
                            value={progCode}
                            disabled={isDisabled}
                          >
                            {p.displayName || p.name}
                          </MenuItem>
                        );
                      })}
                  </Select>
                </FormControl>
              ) : (
                <Typography
                  sx={{ fontWeight: 600, color: "#4B3F72", fontSize: "14px" }}
                >
                  {getProgramDisplayName(selectedProgram)}
                </Typography>
              )}
            </Box>
          )}

          {/* Template Name input (for all program templates) */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography
              sx={{ fontWeight: 500, color: "#2D2D2D", fontSize: "14px" }}
            >
              Template Name:
            </Typography>
            <TextField
              size="small"
              variant="outlined"
              placeholder="Enter Template Name"
              slotProps={{ htmlInput: { maxLength: 99 } }}
              value={
                activeGroup?.groupName ||
                currentTemplate.name ||
                currentTemplate.form?.title ||
                ""
              }
              onChange={(e) => {
                const newName = e.target.value;
                updateCurrentTemplate({
                  ...currentTemplate,
                  name: newName,
                  form: { ...currentTemplate.form, title: newName },
                });
                if (activeGroup) {
                  setActiveGroup({
                    ...activeGroup,
                    groupName: newName,
                  });
                }
              }}
              sx={{
                width: "250px",
                "& .MuiOutlinedInput-root": {
                  height: "40px",
                  borderRadius: "10px",
                  fontSize: "13px",
                  fontWeight: 400,
                  backgroundColor: "#ffffff",
                  "& fieldset": { borderColor: "#e8e8e9" },
                  "&:hover fieldset": { borderColor: "#403c6f" },
                  "&.Mui-focused fieldset": { borderColor: "#403c6f" },
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "#403c6f",
                  opacity: 0.5,
                  fontWeight: 500,
                  fontSize: "12px",
                },
              }}
            />
          </Box>

          {/* Assessment Type Selection (always for new, or for program-specific templates) */}
          {(isNewConfig || !isGeneral || isGeneral) && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Typography
                sx={{ fontWeight: 500, color: "#2D2D2D", fontSize: "14px" }}
              >
                Assessment type:
              </Typography>
              <FormControl size="small" sx={{ width: "250px" }}>
                <Select
                  displayEmpty
                  value={selectedType}
                  onChange={(e) => {
                    handleTypeChangeWithCheck(e.target.value);
                  }}
                  sx={{
                    borderRadius: "10px",
                    fontSize: "14px",
                    fontWeight: 600,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#E8E8E9",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: primaryColor,
                    },
                  }}
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <span style={{ color: "#9e9e9e" }}>Select Type</span>
                      );
                    }
                    return getAssessmentTypeDisplayName(selected);
                  }}
                >
                  {assessmentTypesList.map((t) => {
                    const val = String(t).toLowerCase();
                    return (
                      <MenuItem key={t} value={val}>
                        {getAssessmentTypeDisplayName(t)}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>
      </Paper>

      <Box
        sx={{
          display: "flex",
          gap: 4,
          flexDirection: { xs: "column", md: "row" },
          alignItems: "stretch",
          width: "100%",
        }} // Main content area
      >
        {/* Sidebar - Sections */}
        <Box
          sx={{
            width: { xs: "100%", md: "400px" },
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Paper
            sx={{
              p: 2.5,
              borderRadius: "16px",
              border: `1px solid ${borderColor}`,
              boxShadow: "none",
              display: "flex",
              flexDirection: "column",
              flex: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2.5,
              }}
            >
              <Typography
                sx={{ fontWeight: 700, fontSize: "16px", color: "#1b2f3f" }}
              >
                Sections
              </Typography>
              <IconButton
                size="small"
                onClick={handleAddSection}
                disabled={!selectedProgram || !selectedType}
                sx={{
                  color: primaryColor,
                  backgroundColor: "rgba(64, 60, 111, 0.05)",
                  borderRadius: "8px",
                }}
              >
                <Plus size={18} />
              </IconButton>{" "}
              {/* Add Section Button */}
            </Box>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                maxHeight: "calc(100vh - 320px)",
                overflowY: "auto",
                pr: 0.5,
                "&::-webkit-scrollbar": { width: "4px" },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#ECECF1",
                  borderRadius: "4px",
                },
              }}
            >
              <List
                sx={{
                  pt: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                }}
              >
                {visibleSectionEntries.map(([key, section]) => {
                  if (!section) return null;

                  if (editingSectionKey === key) {
                    return (
                      <ListItem key={key} disablePadding sx={{ py: 0.5 }}>
                        <TextField
                          autoFocus
                          size="small"
                          value={editSectionValue}
                          onChange={(e) => setEditSectionValue(e.target.value)}
                          onBlur={() => {
                            if (!isSavingRef.current) {
                              handleSaveSectionEdit(key, editSectionValue);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              handleSaveSectionEdit(key, editSectionValue);
                            } else if (e.key === "Escape") {
                              setEditingSectionKey(null);
                              setEditSectionValue("");
                            }
                          }}
                          placeholder="Enter section name..."
                          fullWidth
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "10px",
                              backgroundColor: "#fff",
                              fontSize: "14px",
                              height: "42px",
                              "& fieldset": { borderColor: primaryColor },
                              "&:hover fieldset": { borderColor: primaryColor },
                              "&.Mui-focused fieldset": {
                                borderColor: primaryColor,
                              },
                            },
                          }}
                        />
                      </ListItem>
                    );
                  }

                  return (
                    <ListItem
                      key={key}
                      disablePadding
                      draggable
                      onDragStart={(e) => handleSectionDragStart(e, key)}
                      onDragOver={(e) => handleSectionDragOver(e, key)}
                      onDragEnd={() => setDraggedSectionKey(null)}
                      onDrop={(e) => handleFieldDropOnSection(e, key)}
                      sx={{
                        cursor: "move",
                        "&:hover .section-action-icon": { opacity: 1 },
                      }}
                    >
                      <ListItemButton
                        selected={selectedSectionKey === key}
                        onClick={() => setSelectedSectionKey(key)}
                        sx={{
                          borderRadius: "10px",
                          py: 1.2,
                          gap: 1.5,
                          "&.Mui-selected": {
                            backgroundColor: "rgba(64, 60, 111, 0.08)",
                            color: primaryColor,
                            "& .MuiListItemText-primary": { fontWeight: 700 },
                            "&:hover": {
                              backgroundColor: "rgba(64, 60, 111, 0.12)",
                            },
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor:
                              selectedSectionKey === key
                                ? "rgba(64, 60, 111, 0.1)"
                                : "#F0F0F5",
                            borderRadius: "6px",
                            p: 0.5,
                            color:
                              selectedSectionKey === key
                                ? primaryColor
                                : "text.secondary",
                          }}
                        >
                          <GripVertical size={16} />
                        </Box>
                        <ListItemText
                          primary={section.label || "Untitled Section"}
                          slotProps={{
                            primary: {
                              fontSize: "14px",
                              fontWeight: 500,
                              sx: {
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                              },
                            },
                          }}
                          sx={{ m: 0 }}
                        />
                        <Box // Section actions (MoreVertical)
                          className="section-action-icon"
                          sx={{
                            opacity:
                              selectedSectionKey === key ||
                              sectionMenuKey === key
                                ? 1
                                : 0,
                            transition: "opacity 0.2s",
                            display: "flex",
                            ml: "auto",
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSectionMenuAnchor(e.currentTarget);
                              setSectionMenuKey(key);
                            }}
                            sx={{ color: "text.secondary", p: 0.5 }}
                          >
                            <MoreVertical size={16} />
                          </IconButton>
                        </Box>
                      </ListItemButton>{" "}
                      {/* End ListItemButton */}
                    </ListItem>
                  );
                })}
                {isInlineAdding && (
                  <ListItem disablePadding sx={{ py: 0.5 }}>
                    <TextField
                      autoFocus
                      size="small"
                      value={inlineSectionValue}
                      onChange={(e) => setInlineSectionValue(e.target.value)}
                      onBlur={() => {
                        if (!isSavingRef.current) {
                          setIsInlineAdding(false);
                          setInlineSectionValue("");
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          isSavingRef.current = true;
                          const trimmed = inlineSectionValue.trim();
                          if (trimmed) {
                            const key = `new_section_${Date.now()}`;
                            const updatedSections = { ...sections };
                            updatedSections[key] = {
                              label: trimmed,
                              fields: {},
                              assessmentType: selectedType,
                            };
                            updateCurrentTemplate({
                              ...currentTemplate,
                              sections: updatedSections,
                            });
                            setSelectedSectionKey(key); // Select the newly added section
                          }
                          setIsInlineAdding(false);
                          setInlineSectionValue("");
                          setTimeout(() => {
                            isSavingRef.current = false;
                          }, 100);
                        } else if (e.key === "Escape") {
                          setIsInlineAdding(false);
                          setInlineSectionValue("");
                        }
                      }}
                      placeholder="Enter section name..."
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          backgroundColor: "#fff",
                          fontSize: "14px",
                          height: "42px",
                          "& fieldset": { borderColor: primaryColor },
                          "&:hover fieldset": { borderColor: primaryColor },
                          "&.Mui-focused fieldset": {
                            borderColor: primaryColor,
                          },
                        },
                      }}
                    />
                  </ListItem>
                )}
                {visibleSectionEntries.length === 0 && !isInlineAdding && (
                  <Box
                    sx={{
                      py: 6,
                      textAlign: "center",
                      backgroundColor: "#F9FAFB",
                      borderRadius: "12px",
                      border: `2px dashed ${borderColor}`,
                    }}
                  >
                    {" "}
                    {/* No sections message */}
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "text.secondary",
                      }}
                    >
                      No sections yet.
                    </Typography>
                  </Box>
                )}
              </List>
            </Box>
          </Paper>
        </Box>

        {/* Main Area - Fields */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Paper
            sx={{
              p: 3,
              borderRadius: "12px",
              border: `1px solid ${borderColor}`,
              boxShadow: "none",
              display: "flex",
              flexDirection: "column",
              flex: 1,
            }}
          >
            {renderMainFieldsArea()}
          </Paper>{" "}
          {/* End Main Area - Fields */}
        </Box>
      </Box>

      <Dialog
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              width: { xs: "95%", md: "600px" },
              maxWidth: "90vw",
              borderRadius: "16px",
              p: { xs: "16px", md: "24px" },
              backgroundColor: "#F7F8FB",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 0,
            m: 0,
            width: "100%",
            height: "40px",
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "20px",
              lineHeight: "30px",
              color: "#000000",
            }}
          >
            {editingField?.key.startsWith("new_")
              ? "Add Question"
              : "Edit Question"}
          </Typography>{" "}
          {/* Dialog Title */}
          <IconButton
            onClick={() => setIsEditModalOpen(false)}
            aria-label="Close edit question dialog"
          >
            <Plus size={20} style={{ transform: "rotate(45deg)" }} />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            borderRadius: "16px",
            border: "1px solid #E0E0E0",
            backgroundColor: "#FFFFFF",
            mt: 2,
            p: 2.5,
            overflowY: "auto",
          }}
        >
          <Box
            sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 0.5 }}
          >
            <Box>
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "35px",
                  color: "#403C6F",
                }}
              >
                Question
              </Typography>
              <TextField
                placeholder="Question Label" // Question Label Input
                fullWidth
                size="small"
                value={editingField?.label || ""}
                onChange={(e) =>
                  setEditingField({ ...editingField, label: e.target.value })
                }
                sx={modalInputStyle}
              />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "35px",
                  color: "#403C6F",
                }}
              >
                Type
              </Typography>
              <FormControl fullWidth size="small">
                <Select // Question Type Select
                  value={editingField?.type || "text"}
                  onChange={(e) =>
                    setEditingField({ ...editingField, type: e.target.value })
                  }
                  sx={modalSelectStyle}
                >
                  <MenuItem value="text">Text Input</MenuItem>
                  <MenuItem value="textarea">Multi-line Text</MenuItem>
                  <MenuItem value="number">Number</MenuItem>
                  <MenuItem value="date">Date</MenuItem>
                  <MenuItem value="time">Time</MenuItem>
                  <MenuItem value="checkbox">Checkbox</MenuItem>
                  <MenuItem value="boolean">Boolean (Yes/No)</MenuItem>
                  <MenuItem value="single_select">
                    Single Select (Dropdown)
                  </MenuItem>
                  <MenuItem value="multi_select">
                    Multi Select (Dropdown)
                  </MenuItem>
                  <MenuItem value="group">Group</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {/* Existing Conditional Visibility Section */}

            {/* NEW Conditional Branching Section for sub-questions */}

            {editingField?.type === "group" && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "14px",
                      lineHeight: "35px",
                      color: "#403C6F",
                    }}
                  >
                    Sub-Fields
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<Plus size={16} />}
                    onClick={() => {
                      const currentFields = editingField.fields || {};
                      const newKey = `sub_field_${Date.now()}`;
                      setEditingField({
                        ...editingField,
                        fields: {
                          ...currentFields,
                          [newKey]: { label: "", type: "text", value: "" },
                        },
                      });
                    }}
                    sx={{
                      color: primaryColor,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "13px",
                      "&:hover": { backgroundColor: "rgba(64, 60, 111, 0.05)" },
                    }}
                  >
                    {" "}
                    {/* Add Sub-Field Button */}
                    Add Sub-Field
                  </Button>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    maxHeight: "250px",
                    overflowY: "auto",
                    pr: 0.5,
                  }}
                >
                  {Object.keys(editingField.fields || {}).length === 0 && (
                    <Box
                      sx={{
                        py: 3,
                        textAlign: "center",
                        backgroundColor: "#F9FAFB",
                        borderRadius: "8px",
                        border: `1px dashed ${borderColor}`,
                      }}
                    >
                      <Typography
                        sx={{ fontSize: "12px", color: "text.secondary" }}
                      >
                        No sub-fields added yet.
                      </Typography>
                    </Box>
                  )}
                  {Object.keys(editingField.fields || {}).map((subKey) => {
                    const subField = editingField.fields[subKey]; // Sub-Field Item
                    return (
                      <React.Fragment key={subKey}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            backgroundColor: "#F9FAFB",
                            p: 1,
                            borderRadius: "8px",
                            border: "1px solid #E8E8E9",
                            "&:hover": { borderColor: primaryColor },
                          }}
                        >
                          <TextField // Sub-Field Label
                            size="small"
                            placeholder="Sub-field label"
                            value={subField.label || ""}
                            onChange={(e) => {
                              setEditingField({
                                ...editingField,
                                fields: {
                                  ...editingField.fields,
                                  [subKey]: {
                                    ...subField,
                                    label: e.target.value,
                                  },
                                },
                              });
                            }}
                            sx={{
                              flex: 1,
                              "& .MuiOutlinedInput-root": {
                                height: "36px",
                                borderRadius: "6px",
                                backgroundColor: "#fff",
                                fontSize: "13px",
                                "& fieldset": { borderColor: "#e8e8e9" },
                                "&:hover fieldset": {
                                  borderColor: primaryColor,
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: primaryColor,
                                },
                              },
                            }}
                          />
                          <FormControl size="small" sx={{ minWidth: "140px" }}>
                            <Select // Sub-Field Type
                              value={subField.type || "text"}
                              onChange={(e) => {
                                setEditingField({
                                  ...editingField,
                                  fields: {
                                    ...editingField.fields,
                                    [subKey]: {
                                      ...subField,
                                      type: e.target.value,
                                      options:
                                        e.target.value === "single_select" ||
                                        e.target.value === "multi_select"
                                          ? subField.options || []
                                          : undefined,
                                    },
                                  },
                                });
                              }}
                              sx={{
                                height: "36px",
                                borderRadius: "6px",
                                fontSize: "13px",
                                backgroundColor: "#fff",
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#e8e8e9",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                  borderColor: primaryColor,
                                },
                              }}
                            >
                              <MenuItem value="text">Text</MenuItem>
                              <MenuItem value="number">Number</MenuItem>
                              <MenuItem value="date">Date</MenuItem>
                              <MenuItem value="time">Time</MenuItem>
                              <MenuItem value="checkbox">Checkbox</MenuItem>
                              <MenuItem value="boolean">
                                Boolean (Yes/No)
                              </MenuItem>
                              <MenuItem value="single_select">
                                Single Select
                              </MenuItem>
                              <MenuItem value="multi_select">
                                Multi Select
                              </MenuItem>
                            </Select>
                          </FormControl>
                          <IconButton // Delete Sub-Field Button
                            size="small"
                            color="error"
                            onClick={() => {
                              const updatedFields = { ...editingField.fields };
                              delete updatedFields[subKey];
                              setEditingField({
                                ...editingField,
                                fields: updatedFields,
                              });
                            }}
                            sx={{
                              p: 0.5,
                              "&:hover": {
                                backgroundColor: "rgba(211, 47, 47, 0.05)",
                              },
                            }}
                          >
                            <Trash2 size={16} />
                          </IconButton>
                        </Box>
                        {/* Options Editor for single_select / multi_select sub-fields */}
                        {(subField.type === "single_select" ||
                          subField.type === "multi_select") && (
                          <Box
                            sx={{
                              mt: 1,
                              ml: 1,
                              pl: 1.5,
                              borderLeft: `2px solid ${primaryColor}`,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 0.5,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "12px",
                                  color: "#403C6F",
                                  fontWeight: 600,
                                }}
                              >
                                Options
                              </Typography>
                              <Button
                                size="small"
                                startIcon={<Plus size={12} />}
                                onClick={() => {
                                  const currentOpts = subField.options || [];
                                  setEditingField({
                                    ...editingField,
                                    fields: {
                                      ...editingField.fields,
                                      [subKey]: {
                                        ...subField,
                                        options: [
                                          ...currentOpts,
                                          { label: "", is_selected: false },
                                        ],
                                      },
                                    },
                                  });
                                }}
                                sx={{
                                  color: primaryColor,
                                  textTransform: "none",
                                  fontWeight: 600,
                                  fontSize: "11px",
                                  py: 0,
                                  "& svg": { color: primaryColor },
                                }}
                              >
                                Add Option
                              </Button>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.75,
                              }}
                            >
                              {(subField.options || []).map((opt, optIdx) => (
                                <Box
                                  key={getOptionKey(opt, optIdx)}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <TextField
                                    fullWidth
                                    size="small"
                                    placeholder={`Option ${optIdx + 1}`}
                                    value={opt.label || ""}
                                    onChange={(e) => {
                                      const newOpts = [
                                        ...(subField.options || []),
                                      ];
                                      newOpts[optIdx] = {
                                        ...newOpts[optIdx],
                                        label: e.target.value,
                                      };
                                      setEditingField({
                                        ...editingField,
                                        fields: {
                                          ...editingField.fields,
                                          [subKey]: {
                                            ...subField,
                                            options: newOpts,
                                          },
                                        },
                                      });
                                    }}
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        height: "30px",
                                        borderRadius: "6px",
                                        backgroundColor: "#fff",
                                        fontSize: "12px",
                                      },
                                    }}
                                  />
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => {
                                      const newOpts = [
                                        ...(subField.options || []),
                                      ];
                                      newOpts.splice(optIdx, 1);
                                      setEditingField({
                                        ...editingField,
                                        fields: {
                                          ...editingField.fields,
                                          [subKey]: {
                                            ...subField,
                                            options: newOpts,
                                          },
                                        },
                                      });
                                    }}
                                  >
                                    <Trash2 size={14} />
                                  </IconButton>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        )}
                      </React.Fragment>
                    );
                  })}
                </Box>
              </Box> // End Group Type Specific Fields
            )}

            {(editingField?.type === "single_select" ||
              editingField?.type === "multi_select") && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "14px",
                      lineHeight: "35px",
                      color: "#403C6F",
                    }}
                  >
                    Options
                  </Typography>
                  <Button
                    size="small" // Add Option Button
                    startIcon={<Plus size={16} />}
                    onClick={() => {
                      const currentOptions = editingField.options || [];
                      setEditingField({
                        ...editingField,
                        options: [
                          ...currentOptions,
                          { label: "", is_selected: false },
                        ],
                      });
                    }}
                    sx={{
                      color: primaryColor,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "13px",
                      "&:hover": { backgroundColor: "rgba(64, 60, 111, 0.05)" },
                    }}
                  >
                    Add Option
                  </Button>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    maxHeight: "250px",
                    overflowY: "auto",
                    pr: 0.5,
                  }}
                >
                  {(editingField.options || []).map((option, index) => (
                    <Box
                      key={getOptionKey(option, index)}
                      draggable
                      onDragStart={(e) => handleOptionDragStart(e, index)}
                      onDragOver={(e) => handleOptionDragOver(e, index)}
                      onDragEnd={() => setDraggedOptionIndex(null)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        backgroundColor: "#F9FAFB",
                        p: 1,
                        borderRadius: "8px",
                        border: "1px solid #E8E8E9",
                        "&:hover": {
                          borderColor: primaryColor,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          cursor: "grab",
                          color: "text.secondary",
                          "&:active": { cursor: "grabbing" },
                        }}
                      >
                        <GripVertical size={16} />
                      </Box>{" "}
                      {/* Drag Handle */}
                      <TextField
                        fullWidth
                        size="small"
                        placeholder={`Option ${index + 1}`}
                        value={option.label || ""}
                        onChange={(e) => {
                          const newOptions = [...editingField.options];
                          newOptions[index] = {
                            ...newOptions[index],
                            label: e.target.value,
                          };
                          setEditingField({
                            ...editingField,
                            options: newOptions,
                          });
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            height: "36px",
                            borderRadius: "6px",
                            backgroundColor: "#fff",
                            fontSize: "13px",
                            "& fieldset": { borderColor: "#e8e8e9" },
                            "&:hover fieldset": { borderColor: primaryColor },
                            "&.Mui-focused fieldset": {
                              borderColor: primaryColor,
                            },
                          },
                        }}
                      />
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          const newOptions = [...editingField.options];
                          newOptions.splice(index, 1);
                          setEditingField({
                            ...editingField,
                            options: newOptions,
                          });
                        }}
                        sx={{
                          p: 0.5,
                          "&:hover": {
                            backgroundColor: "rgba(211, 47, 47, 0.05)",
                          },
                        }}
                      >
                        <Trash2 size={16} />
                      </IconButton>{" "}
                      {/* Delete Option Button */}
                    </Box> // End Option Item
                  ))}

                  {(!editingField.options ||
                    editingField.options.length === 0) && ( // No Options Message
                    <Box
                      sx={{
                        py: 3,
                        textAlign: "center",
                        backgroundColor: "#F9FAFB",
                        borderRadius: "8px",
                        border: `1px dashed ${borderColor}`,
                      }}
                    >
                      <Typography
                        sx={{ fontSize: "12px", color: "text.secondary" }}
                      >
                        No options added yet.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            <Box sx={{ display: "flex", gap: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={editingField?.required || false}
                    onChange={(e) =>
                      setEditingField({
                        ...editingField,
                        required: e.target.checked,
                      })
                    }
                    sx={{
                      color: primaryColor,
                      "&.Mui-checked": { color: `${primaryColor} !important` },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 500 }}
                  >
                    Required Field
                  </Typography>
                }
              />
              {/* Conditional Branching Checkbox (for boolean, single_select, or multi_select) */}
              {(editingField?.type === "boolean" ||
                editingField?.type === "single_select" ||
                editingField?.type === "multi_select") && (
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={!!editingField?.branches}
                      onChange={(e) => {
                        setEditingField({
                          ...editingField,
                          branches: e.target.checked ? {} : null,
                        });
                      }}
                      sx={{
                        color: primaryColor,
                        "&.Mui-checked": {
                          color: `${primaryColor} !important`,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        fontSize: "13px",
                        color: "#403C6F",
                        fontWeight: 500,
                      }}
                    >
                      Conditional Branching
                    </Typography>
                  }
                />
              )}
            </Box>
          </Box>
          {(editingField?.type === "boolean" ||
            editingField?.type === "single_select" ||
            editingField?.type === "multi_select") &&
            editingField?.branches && (
              <Box sx={{ mt: 3, borderTop: `1px solid ${borderColor}`, pt: 2 }}>
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#403C6F",
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Define Sub-Questions
                </Typography>

                {editingField.type === "boolean" && (
                  <>
                    {["true", "false"].map((answer) => {
                      const subQType =
                        editingField.branches?.[answer]?.subQuestion?.type ||
                        "text";
                      return (
                        <Box
                          key={answer}
                          sx={{
                            mb: 2,
                            p: 2,
                            border: `1px solid ${borderColor}`,
                            borderRadius: "8px",
                            backgroundColor: "#F9FAFB",
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 500,
                              fontSize: "14px",
                              color: "#403C6F",
                              mb: 1,
                            }}
                          >
                            If answer is '{answer === "true" ? "True" : "False"}
                            '
                          </Typography>
                          <TextField
                            placeholder="Sub-Question Label"
                            fullWidth
                            size="small"
                            value={
                              editingField.branches?.[answer]?.subQuestion
                                ?.label || ""
                            }
                            onChange={(e) => {
                              setEditingField((prev) => ({
                                ...prev,
                                branches: {
                                  ...prev.branches,
                                  [answer]: {
                                    subQuestion: {
                                      ...prev.branches?.[answer]?.subQuestion,
                                      label: e.target.value,
                                      type:
                                        prev.branches?.[answer]?.subQuestion
                                          ?.type || "text", // Default type
                                    },
                                  },
                                },
                              }));
                            }}
                            sx={modalInputStyle}
                          />
                          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                            <Select
                              value={subQType}
                              onChange={(e) => {
                                setEditingField((prev) => ({
                                  ...prev,
                                  branches: {
                                    ...prev.branches,
                                    [answer]: {
                                      subQuestion: {
                                        ...prev.branches?.[answer]?.subQuestion,
                                        type: e.target.value,
                                      },
                                    },
                                  },
                                }));
                              }}
                              sx={modalSelectStyle}
                            >
                              <MenuItem value="text">Text Input</MenuItem>
                              <MenuItem value="textarea">
                                Multi-line Text
                              </MenuItem>
                              <MenuItem value="number">Number</MenuItem>
                              <MenuItem value="date">Date</MenuItem>
                              <MenuItem value="time">Time</MenuItem>
                              <MenuItem value="checkbox">Checkbox</MenuItem>
                              <MenuItem value="boolean">
                                Boolean (Yes/No)
                              </MenuItem>
                              <MenuItem value="single_select">
                                Single Select (Dropdown)
                              </MenuItem>
                              <MenuItem value="multi_select">
                                Multi Select (Dropdown)
                              </MenuItem>
                            </Select>
                          </FormControl>

                          {/* Sub-Question Options configuration if single_select or multi_select is chosen */}
                          {(subQType === "single_select" ||
                            subQType === "multi_select") && (
                            <Box
                              sx={{
                                mt: 1.5,
                                pl: 1.5,
                                borderLeft: `2px solid ${primaryColor}`,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  mb: 1,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "12px",
                                    color: "#403C6F",
                                    fontWeight: 600,
                                  }}
                                >
                                  Sub-Question Options
                                </Typography>
                                <Button
                                  size="small"
                                  onClick={() => {
                                    const currentOpts =
                                      editingField.branches?.[answer]
                                        ?.subQuestion?.options || [];
                                    setEditingField((prev) => ({
                                      ...prev,
                                      branches: {
                                        ...prev.branches,
                                        [answer]: {
                                          subQuestion: {
                                            ...prev.branches?.[answer]
                                              ?.subQuestion,
                                            options: [
                                              ...currentOpts,
                                              { label: "", is_selected: false },
                                            ],
                                          },
                                        },
                                      },
                                    }));
                                  }}
                                  sx={{
                                    color: primaryColor,
                                    textTransform: "none",
                                    fontWeight: 600,
                                    fontSize: "11px",
                                    py: 0,
                                  }}
                                >
                                  Add Option
                                </Button>
                              </Box>

                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 1,
                                }}
                              >
                                {(
                                  editingField.branches?.[answer]?.subQuestion
                                    ?.options || []
                                ).map((subOpt, subOptIdx) => (
                                  <Box
                                    key={getOptionKey(subOpt, subOptIdx)}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <TextField
                                      fullWidth
                                      size="small"
                                      placeholder={`Option ${subOptIdx + 1}`}
                                      value={subOpt.label || ""}
                                      onChange={(e) =>
                                        handleBranchOptionChange(
                                          answer,
                                          subOptIdx,
                                          e.target.value,
                                        )
                                      }
                                      sx={{
                                        "& .MuiOutlinedInput-root": {
                                          height: "30px",
                                          borderRadius: "6px",
                                          backgroundColor: "#fff",
                                          fontSize: "12px",
                                        },
                                      }}
                                    />
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() =>
                                        handleBranchOptionDelete(
                                          answer,
                                          subOptIdx,
                                        )
                                      }
                                    >
                                      <Trash2 size={14} />
                                    </IconButton>
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </>
                )}

                {(editingField.type === "single_select" ||
                  editingField.type === "multi_select") && (
                  <>
                    {(editingField.options || []).map((option) => {
                      const subQType =
                        editingField.branches?.[option.label]?.subQuestion
                          ?.type || "text";
                      return (
                        <Box
                          key={option.label}
                          sx={{
                            mb: 2,
                            p: 2,
                            border: `1px solid ${borderColor}`,
                            borderRadius: "8px",
                            backgroundColor: "#F9FAFB",
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 500,
                              fontSize: "14px",
                              color: "#403C6F",
                              mb: 1,
                            }}
                          >
                            If answer is '{option.label}'
                          </Typography>
                          <TextField
                            placeholder="Sub-Question Label"
                            fullWidth
                            size="small"
                            value={
                              editingField.branches?.[option.label]?.subQuestion
                                ?.label || ""
                            }
                            onChange={(e) => {
                              setEditingField((prev) => ({
                                ...prev,
                                branches: {
                                  ...prev.branches,
                                  [option.label]: {
                                    subQuestion: {
                                      ...prev.branches?.[option.label]
                                        ?.subQuestion,
                                      label: e.target.value,
                                      type:
                                        prev.branches?.[option.label]
                                          ?.subQuestion?.type || "text", // Default type
                                    },
                                  },
                                },
                              }));
                            }}
                            sx={modalInputStyle}
                          />
                          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                            <Select
                              value={subQType}
                              onChange={(e) => {
                                setEditingField((prev) => ({
                                  ...prev,
                                  branches: {
                                    ...prev.branches,
                                    [option.label]: {
                                      subQuestion: {
                                        ...prev.branches?.[option.label]
                                          ?.subQuestion,
                                        type: e.target.value,
                                      },
                                    },
                                  },
                                }));
                              }}
                              sx={modalSelectStyle}
                            >
                              <MenuItem value="text">Text Input</MenuItem>
                              <MenuItem value="textarea">
                                Multi-line Text
                              </MenuItem>
                              <MenuItem value="number">Number</MenuItem>
                              <MenuItem value="date">Date</MenuItem>
                              <MenuItem value="time">Time</MenuItem>
                              <MenuItem value="checkbox">Checkbox</MenuItem>
                              <MenuItem value="boolean">
                                Boolean (Yes/No)
                              </MenuItem>
                              <MenuItem value="single_select">
                                Single Select (Dropdown)
                              </MenuItem>
                              <MenuItem value="multi_select">
                                Multi Select (Dropdown)
                              </MenuItem>
                            </Select>
                          </FormControl>

                          {/* Sub-Question Options configuration if single_select or multi_select is chosen */}
                          {(subQType === "single_select" ||
                            subQType === "multi_select") && (
                            <Box
                              sx={{
                                mt: 1.5,
                                pl: 1.5,
                                borderLeft: `2px solid ${primaryColor}`,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  mb: 1,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "12px",
                                    color: "#403C6F",
                                    fontWeight: 600,
                                  }}
                                >
                                  Sub-Question Options
                                </Typography>
                                <Button
                                  size="small"
                                  onClick={() => {
                                    const currentOpts =
                                      editingField.branches?.[option.label]
                                        ?.subQuestion?.options || [];
                                    setEditingField((prev) => ({
                                      ...prev,
                                      branches: {
                                        ...prev.branches,
                                        [option.label]: {
                                          subQuestion: {
                                            ...prev.branches?.[option.label]
                                              ?.subQuestion,
                                            options: [
                                              ...currentOpts,
                                              { label: "", is_selected: false },
                                            ],
                                          },
                                        },
                                      },
                                    }));
                                  }}
                                  sx={{
                                    color: primaryColor,
                                    textTransform: "none",
                                    fontWeight: 600,
                                    fontSize: "11px",
                                    py: 0,
                                  }}
                                >
                                  Add Option
                                </Button>
                              </Box>

                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 1,
                                }}
                              >
                                {(
                                  editingField.branches?.[option.label]
                                    ?.subQuestion?.options || []
                                ).map((subOpt, subOptIdx) => (
                                  <Box
                                    key={getOptionKey(subOpt, subOptIdx)}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <TextField
                                      fullWidth
                                      size="small"
                                      placeholder={`Option ${subOptIdx + 1}`}
                                      value={subOpt.label || ""}
                                      onChange={(e) =>
                                        handleBranchOptionChange(
                                          option.label,
                                          subOptIdx,
                                          e.target.value,
                                        )
                                      }
                                      sx={{
                                        "& .MuiOutlinedInput-root": {
                                          height: "30px",
                                          borderRadius: "6px",
                                          backgroundColor: "#fff",
                                          fontSize: "12px",
                                        },
                                      }}
                                    />
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() =>
                                        handleBranchOptionDelete(
                                          option.label,
                                          subOptIdx,
                                        )
                                      }
                                    >
                                      <Trash2 size={14} />
                                    </IconButton>
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </>
                )}
              </Box>
            )}
        </DialogContent>
        <DialogActions
          sx={{ justifyContent: "flex-end", gap: "10px", mt: 2, p: 0 }}
        >
          <Button
            onClick={() => setIsEditModalOpen(false)}
            variant="outlined"
            sx={{
              borderColor: "#E8E8E9",
              color: "#6E6D92",
              textTransform: "none",
              fontWeight: 500,
              px: 3,
              borderRadius: "52px",
              height: "36px",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveField}
            variant="contained"
            sx={{
              borderRadius: "52px",
              backgroundColor: primaryColor,
              color: "#FFFFFF",
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 600,
              px: 3,
              height: "36px",
              "&:hover": { backgroundColor: "#312e5e" },
            }}
          >
            Save Question
          </Button>
        </DialogActions>
      </Dialog>
      {/* End Edit Field Modal */}

      {/* Section Options Menu */}
      <Menu
        anchorEl={sectionMenuAnchor}
        open={Boolean(sectionMenuAnchor)}
        onClose={() => {
          setSectionMenuAnchor(null);
          setSectionMenuKey(null);
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "8px",
              mt: 0.5,
              minWidth: "150px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            },
          },
        }}
      >
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            setEditingSectionKey(sectionMenuKey);
            const sectionToEdit = sections[sectionMenuKey];
            setEditSectionValue(sectionToEdit?.label || "");
            setSectionMenuAnchor(null);
            setSectionMenuKey(null);
          }}
          sx={{ fontSize: "14px", fontWeight: 500 }}
        >
          <Edit size={16} style={{ marginRight: 8 }} />
          Edit
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            setSectionToDelete(sectionMenuKey);
            setIsDeleteSectionConfirmOpen(true);
            setSectionMenuAnchor(null);
          }}
          sx={{ fontSize: "14px", fontWeight: 500, color: "error.main" }}
        >
          <Trash2 size={16} style={{ marginRight: 8 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Delete Section Confirmation */}
      <Dialog
        open={isDeleteSectionConfirmOpen}
        onClose={() => setIsDeleteSectionConfirmOpen(false)}
      >
        <DialogTitle fontWeight={700}>Delete Section</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body1">
            Are you sure you want to delete this section? All questions inside
            it will be removed.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setIsDeleteSectionConfirmOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteSection}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Unsaved Changes Dialog */}
      <Dialog
        open={isConfirmUnsavedOpen}
        onClose={handleConfirmCancel}
        slotProps={{
          paper: { sx: { borderRadius: "12px", p: 1 } },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            You have unsaved changes in this assessment template. Please save
            your changes before switching programs or assessment types.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleConfirmCancel}
            variant="text"
            sx={{
              color: "#6B7280",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSave}
            variant="contained"
            sx={{
              backgroundColor: primaryColor,
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              "&:hover": { backgroundColor: "#312e5e" },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%", fontWeight: 500 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box> // End AssessmentConfigPage
  );
};

export default AssessmentConfigPage;
